/**
 * Elite Batch 13 Part 5 — Workflow Templates 1228–1247
 * Us vs them comparison video, UGC curation, VIP churn prevention,
 * VIP concierge, VIP early access, VIP product drop, loyalty welcome,
 * video asset library, multi-platform distribution, viral detection,
 * WebEngage orchestration, journey builder, personalized push, relays,
 * revenue attribution, retention, webinar retargeting, registrant follow-up,
 * community roundup, and Google Ads digest workflows.
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 *    NEVER assume webhook payloads have specific field names (no {email}, {name}, etc.)
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  message: string,
  x = 80,
): any {
  return n(id, "sendSMS", label, y, { sendSmsDetails: { to, message } }, x);
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function hubspotGetNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "GET",
        url: `https://api.hubapi.com/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [],
        variables: vars,
      },
    },
    x,
  );
}
function hubspotPostNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  bodyFields: Array<{ key: string; value: string }>,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://api.hubapi.com/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: bodyFields,
        variables: vars,
      },
    },
    x,
  );
}

// =============================================================================
// ELITE BATCH 13 PART 5 — TEMPLATES 1228–1247
// =============================================================================

export const ELITE_BATCH13_PART5: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 1228. US VS. THEM COMPARISON VIDEO (8 nodes)
  // ===================================================================

  "Us vs. Them Comparison Video": {
    summary:
      "Takes a comparison brief via manual trigger, fetches competitor data with runCode, generates a balanced comparison video script with AI, checks legal compliance via condition, renders the video through Kling API, routes through a human/legal approval gate, publishes to YouTube via API, and tracks performance in Google Sheets.",
    nodes: [
      trigger("Manual: Comparison video brief submitted", "manual", {
        howItStarts:
          "A marketing team member submits a comparison brief specifying the competitor name, product category, key differentiators, and target audience for a us-vs-them comparison video",
        setup:
          "Configure OpenAI API key for script generation. Set Kling AI API key for video rendering. Provide YouTube Data API credentials for publishing. Set Google Sheets spreadsheet ID for performance tracking.",
        inputFields: [
          { name: "competitor_name", type: "string", example: "CompetitorX" },
          {
            name: "product_category",
            type: "string",
            example: "Project Management Software",
          },
          {
            name: "our_strengths",
            type: "string",
            example: "Real-time collaboration, AI automation, 99.9% uptime",
          },
          {
            name: "target_audience",
            type: "string",
            example: "Mid-market SaaS teams 50-200 employees",
          },
          {
            name: "video_tone",
            type: "string",
            example: "Professional, factual, non-aggressive",
          },
        ],
      }),

      // 2. Competitor data fetcher (runCode)
      n("2", "runCode", "Structure competitor comparison data", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Structure competitor comparison data from the manual brief
const competitor = vars.competitor_name || "Unknown";
const category = vars.product_category || "Software";
const strengths = vars.our_strengths || "";
const strengthsList = strengths.split(",").map(s => s.trim()).filter(Boolean);
const comparisonPoints = strengthsList.length;
const timestamp = new Date().toISOString();
const videoId = "cmp_" + Date.now().toString(36);
return {
  structured_competitor: competitor,
  structured_category: category,
  comparison_point_count: comparisonPoints,
  strengths_json: JSON.stringify(strengthsList),
  video_ref_id: videoId,
  brief_timestamp: timestamp,
  compliance_required: comparisonPoints > 0 ? "yes" : "no"
};`,
          outputVariables: [
            { name: "structured_competitor", type: "string" },
            { name: "structured_category", type: "string" },
            { name: "comparison_point_count", type: "number" },
            { name: "strengths_json", type: "string" },
            { name: "video_ref_id", type: "string" },
            { name: "brief_timestamp", type: "string" },
            { name: "compliance_required", type: "string" },
          ],
        },
      }),

      // 3. AI comparison script generator
      aiNode(
        "3",
        "AI: Generate comparison video script",
        520,
        "You are a video scriptwriter specializing in product comparison content. Write scripts that are factual, balanced, and compelling. Never make unsubstantiated claims. Always cite verifiable features. Return JSON.",
        "Write a comparison video script for {structured_category}. Our product vs {structured_competitor}. Our strengths: {strengths_json}. Target audience: {target_audience}. Tone: {video_tone}. Return video_title (compelling, under 60 chars), hook_line (first 5 seconds grabs attention), comparison_script (full 60-90 second script with scene directions), cta_text (call to action), legal_disclaimers (any needed disclaimers about claims), compliance_safe (true/false whether all claims are verifiable).",
        [
          { field: "video_title", as: "video_title", type: "string" },
          { field: "hook_line", as: "hook_line", type: "string" },
          {
            field: "comparison_script",
            as: "comparison_script",
            type: "string",
          },
          { field: "cta_text", as: "cta_text", type: "string" },
          {
            field: "legal_disclaimers",
            as: "legal_disclaimers",
            type: "string",
          },
          { field: "compliance_safe", as: "compliance_safe", type: "string" },
        ],
      ),

      // 4. Legal compliance checker (condition)
      n(
        "4",
        "condition",
        "Check legal compliance: all claims verifiable",
        760,
        {
          conditionDetails: {
            conditions: [
              {
                variable: "{compliance_safe}",
                operator: "equals",
                value: "true",
              },
            ],
          },
        },
      ),

      // 5. Kling video renderer API
      n("5", "hitApi", "Render comparison video via Kling AI", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.klingai.com/v1/videos/text2video",
          headers: [
            { key: "Authorization", value: "Bearer {kling_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "prompt", value: "{comparison_script}" },
            { key: "duration", value: "90" },
            { key: "aspect_ratio", value: "16:9" },
            { key: "model", value: "kling-v1" },
          ],
          variables: [
            {
              name: "data.task_id",
              customName: "kling_task_id",
              type: "string",
            },
            {
              name: "data.video_url",
              customName: "rendered_video_url",
              type: "string",
            },
          ],
        },
      }),

      // 6. Human/legal approval gate
      n("6", "approvalGate", "Legal and Marketing Approval Required", 1240, {
        approvalGateDetails: {
          approvers: ["legal-team@company.com", "marketing-lead@company.com"],
          message:
            "Comparison video for {structured_competitor} ready for review.\\n\\nTitle: {video_title}\\nHook: {hook_line}\\nDisclaimers: {legal_disclaimers}\\nVideo URL: {rendered_video_url}\\n\\nPlease review for legal compliance and brand alignment before publishing.",
          timeoutHours: 48,
        },
      }),

      // 7. Multi-platform publisher API (YouTube)
      n("7", "hitApi", "Publish comparison video to YouTube", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://www.googleapis.com/upload/youtube/v3/videos?uploadType=resumable&part=snippet,status",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "snippet",
              value:
                '{"title":"{video_title}","description":"{comparison_script}\\n\\n{legal_disclaimers}","tags":["comparison","{structured_category}","{structured_competitor}"],"categoryId":"28"}',
            },
            {
              key: "status",
              value:
                '{"privacyStatus":"public","selfDeclaredMadeForKids":false}',
            },
          ],
          variables: [
            { name: "id", customName: "youtube_video_id", type: "string" },
            {
              name: "snippet.title",
              customName: "published_title",
              type: "string",
            },
          ],
        },
      }),

      // 8. Performance/sentiment monitor (Sheets)
      sheetsNode(
        "8",
        "Log video performance to Google Sheets",
        1720,
        "ComparisonVideos",
        '[["{brief_timestamp}","{video_ref_id}","{structured_competitor}","{structured_category}","{video_title}","{youtube_video_id}","{rendered_video_url}","{comparison_point_count}","published"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1229. USER GENERATED CONTENT CURATOR (8 nodes)
  // ===================================================================

  "User Generated Content Curator": {
    summary:
      "Runs on a daily schedule to discover UGC from Instagram, scores content quality with runCode, selects top-performing content via condition, checks rights/permission status via condition, organizes approved content in a Sheets library, distributes curated content via email newsletter, and sends creator recognition emails.",
    nodes: [
      trigger("Schedule: Daily UGC discovery scan", "schedule", {
        schedule: "0 9 * * * (Daily at 9 AM)",
        howItStarts:
          "A daily schedule scans Instagram for user-generated content mentioning the brand hashtag or tagging the brand account, collecting posts with images, captions, engagement metrics, and creator handles",
        setup:
          "Configure Instagram Graph API token with business account access. Set Google Sheets spreadsheet ID for content library. Provide email service credentials for distribution and creator outreach.",
        inputFields: [],
      }),

      // 2. Instagram API UGC discovery scanner
      n("2", "hitApi", "Scan Instagram for brand UGC", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://graph.facebook.com/v18.0/{ig_business_id}/tags?fields=id,caption,media_type,media_url,permalink,timestamp,username,like_count,comments_count&limit=50",
          headers: [
            { key: "Authorization", value: "Bearer {meta_access_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "data[0].id", customName: "top_post_id", type: "string" },
            {
              name: "data[0].caption",
              customName: "top_caption",
              type: "string",
            },
            {
              name: "data[0].media_url",
              customName: "top_media_url",
              type: "string",
            },
            {
              name: "data[0].permalink",
              customName: "top_permalink",
              type: "string",
            },
            {
              name: "data[0].username",
              customName: "top_creator_handle",
              type: "string",
            },
            {
              name: "data[0].like_count",
              customName: "top_like_count",
              type: "number",
            },
            {
              name: "data[0].comments_count",
              customName: "top_comments_count",
              type: "number",
            },
            {
              name: "data.length",
              customName: "total_ugc_found",
              type: "number",
            },
          ],
        },
      }),

      // 3. Content quality scorer (runCode)
      n("3", "runCode", "Score content quality and engagement", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Score UGC quality based on engagement metrics and content attributes
const likes = parseInt(vars.top_like_count) || 0;
const comments = parseInt(vars.top_comments_count) || 0;
const totalUgc = parseInt(vars.total_ugc_found) || 0;
const caption = vars.top_caption || "";
const engagementScore = (likes * 1) + (comments * 3);
const captionLength = caption.length;
const hasHashtags = caption.includes("#") ? 10 : 0;
const qualityScore = engagementScore + hasHashtags + Math.min(captionLength / 10, 20);
const meetsThreshold = qualityScore >= 50 ? "yes" : "no";
const tier = qualityScore >= 100 ? "premium" : qualityScore >= 50 ? "standard" : "low";
return {
  engagement_score: engagementScore,
  quality_score: Math.round(qualityScore),
  meets_quality_threshold: meetsThreshold,
  content_tier: tier,
  total_discovered: totalUgc,
  scan_date: new Date().toISOString().split("T")[0]
};`,
          outputVariables: [
            { name: "engagement_score", type: "number" },
            { name: "quality_score", type: "number" },
            { name: "meets_quality_threshold", type: "string" },
            { name: "content_tier", type: "string" },
            { name: "total_discovered", type: "number" },
            { name: "scan_date", type: "string" },
          ],
        },
      }),

      // 4. Top content selector (condition)
      n("4", "condition", "Check quality threshold: score >= 50", 760, {
        conditionDetails: {
          conditions: [
            {
              variable: "{meets_quality_threshold}",
              operator: "equals",
              value: "yes",
            },
          ],
        },
      }),

      // 5. Rights status checker (condition)
      n("5", "condition", "Check rights: content tier is not low", 1000, {
        conditionDetails: {
          conditions: [
            {
              variable: "{content_tier}",
              operator: "not_equals",
              value: "low",
            },
          ],
        },
      }),

      // 6. Content library organizer (Sheets)
      sheetsNode(
        "6",
        "Add approved UGC to content library",
        1240,
        "UGCLibrary",
        '[["{scan_date}","{top_post_id}","{top_creator_handle}","{top_permalink}","{top_media_url}","{top_like_count}","{top_comments_count}","{quality_score}","{content_tier}","pending_rights"]]',
      ),

      // 7. Multi-channel distributor (email)
      emailNode(
        "7",
        "Distribute curated UGC to marketing team",
        1480,
        "{marketing_team_email}",
        "UGC Discovery Report - {scan_date}: {total_discovered} posts found",
        "Hi Team,\\n\\nDaily UGC scan complete for {scan_date}.\\n\\nTotal posts discovered: {total_discovered}\\n\\nTop content highlight:\\n- Creator: @{top_creator_handle}\\n- Engagement score: {engagement_score} (Quality: {quality_score})\\n- Tier: {content_tier}\\n- Post: {top_permalink}\\n\\nThis content has been added to the UGC library for rights clearance.\\n\\nBest,\\nUGC Curator Bot",
      ),

      // 8. Creator recognition sender (email)
      emailNode(
        "8",
        "Send creator recognition email",
        1720,
        "{creator_outreach_email}",
        "We love your content, @{top_creator_handle}!",
        "Hi @{top_creator_handle},\\n\\nWe spotted your amazing post about our brand and loved it! Your content scored in our {content_tier} tier with {top_like_count} likes and {top_comments_count} comments.\\n\\nWe would love to feature your content across our channels. Would you grant us permission to reshare?\\n\\nAs a thank you, we would love to offer you a special creator perk.\\n\\nPost in question: {top_permalink}\\n\\nReply to let us know your preferences.\\n\\nThanks for being an amazing part of our community!\\n\\n{company_name}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("5", "6", "output_1"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1230. VIP CHURN PREVENTION (9 nodes)
  // ===================================================================

  "VIP Churn Prevention": {
    summary:
      "Runs on a weekly schedule to detect churn signals from HubSpot, scores churn risk with runCode, gates on risk threshold via condition, enriches customer history from HubSpot, generates personalized retention offers with AI, sends VIP outreach emails, schedules concierge calls via Slack, and logs outcomes to Google Sheets.",
    nodes: [
      trigger("Schedule: Weekly VIP churn signal scan", "schedule", {
        schedule: "0 8 * * 1 (Monday at 8 AM)",
        howItStarts:
          "A weekly schedule scans VIP customer accounts for churn indicators including declining usage, support ticket spikes, contract renewal proximity, and reduced login frequency",
        setup:
          "Configure HubSpot API key with contacts and deals read access. Set OpenAI API key for retention offer generation. Provide Slack webhook for concierge team alerts. Set Google Sheets spreadsheet ID for outcome tracking.",
        inputFields: [],
      }),

      // 2. HubSpot churn signal detector
      hubspotPostNode(
        "2",
        "Detect VIP churn signals in HubSpot",
        280,
        "crm/v3/objects/contacts/search",
        [
          {
            key: "filterGroups",
            value:
              '[{"filters":[{"propertyName":"hs_lead_status","operator":"EQ","value":"VIP"},{"propertyName":"notes_last_updated","operator":"LT","value":"{thirty_days_ago}"}]}]',
          },
          {
            key: "properties",
            value:
              '["email","firstname","lastname","hs_lead_status","lifecyclestage","num_associated_deals","hs_email_last_open_date","notes_last_updated"]',
          },
          { key: "limit", value: "10" },
        ],
        [
          {
            name: "results[0].properties.email",
            customName: "vip_email",
            type: "string",
          },
          {
            name: "results[0].properties.firstname",
            customName: "vip_first_name",
            type: "string",
          },
          {
            name: "results[0].properties.lastname",
            customName: "vip_last_name",
            type: "string",
          },
          {
            name: "results[0].id",
            customName: "vip_contact_id",
            type: "string",
          },
          {
            name: "results[0].properties.num_associated_deals",
            customName: "deal_count",
            type: "number",
          },
          {
            name: "results[0].properties.hs_email_last_open_date",
            customName: "last_email_open",
            type: "string",
          },
          { name: "total", customName: "at_risk_vip_count", type: "number" },
        ],
      ),

      // 3. Churn risk scorer (runCode)
      n("3", "runCode", "Calculate VIP churn risk score", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Score churn risk based on multiple signals
const lastOpen = vars.last_email_open || "";
const dealCount = parseInt(vars.deal_count) || 0;
const atRiskCount = parseInt(vars.at_risk_vip_count) || 0;
let riskScore = 0;
// No email opens in 30+ days
if (!lastOpen || new Date(lastOpen) < new Date(Date.now() - 30*24*60*60*1000)) riskScore += 35;
// Low deal activity
if (dealCount < 2) riskScore += 25;
// Part of at-risk cohort
if (atRiskCount > 5) riskScore += 15;
// Base risk for being flagged
riskScore += 25;
const riskLevel = riskScore >= 75 ? "critical" : riskScore >= 50 ? "high" : "moderate";
const needsIntervention = riskScore >= 50 ? "yes" : "no";
return {
  churn_risk_score: Math.min(riskScore, 100),
  risk_level: riskLevel,
  needs_intervention: needsIntervention,
  days_since_engagement: lastOpen ? Math.floor((Date.now() - new Date(lastOpen).getTime()) / (24*60*60*1000)) : 90,
  assessment_date: new Date().toISOString()
};`,
          outputVariables: [
            { name: "churn_risk_score", type: "number" },
            { name: "risk_level", type: "string" },
            { name: "needs_intervention", type: "string" },
            { name: "days_since_engagement", type: "number" },
            { name: "assessment_date", type: "string" },
          ],
        },
      }),

      // 4. Risk threshold gate (condition)
      n("4", "condition", "Check risk: needs intervention", 760, {
        conditionDetails: {
          conditions: [
            {
              variable: "{needs_intervention}",
              operator: "equals",
              value: "yes",
            },
          ],
        },
      }),

      // 5. Customer history enricher (HubSpot)
      hubspotGetNode(
        "5",
        "Fetch VIP customer full history",
        1000,
        "crm/v3/objects/contacts/{vip_contact_id}?properties=email,firstname,lastname,company,jobtitle,hs_analytics_revenue,hs_lifecyclestage_customer_date,num_associated_deals",
        [
          {
            name: "properties.company",
            customName: "vip_company",
            type: "string",
          },
          {
            name: "properties.jobtitle",
            customName: "vip_job_title",
            type: "string",
          },
          {
            name: "properties.hs_analytics_revenue",
            customName: "lifetime_revenue",
            type: "string",
          },
          {
            name: "properties.hs_lifecyclestage_customer_date",
            customName: "customer_since",
            type: "string",
          },
        ],
      ),

      // 6. AI retention offer generator
      aiNode(
        "6",
        "AI: Generate personalized retention offer",
        1240,
        "You are a customer retention specialist for high-value B2B accounts. Create personalized retention offers that address specific churn signals. Be empathetic, value-focused, and specific. Return JSON.",
        "Generate a retention offer for VIP customer. Name: {vip_first_name} {vip_last_name}. Company: {vip_company}. Title: {vip_job_title}. Lifetime revenue: {lifetime_revenue}. Customer since: {customer_since}. Churn risk: {risk_level} ({churn_risk_score}/100). Days since engagement: {days_since_engagement}. Deals: {deal_count}. Return offer_subject (email subject), offer_body (personalized retention email with specific offer), offer_type (discount/upgrade/service/consultation), offer_value (specific offer details), urgency_level (how urgent the outreach is), concierge_brief (2-line brief for the concierge team call).",
        [
          { field: "offer_subject", as: "offer_subject", type: "string" },
          { field: "offer_body", as: "offer_body", type: "string" },
          { field: "offer_type", as: "offer_type", type: "string" },
          { field: "offer_value", as: "offer_value", type: "string" },
          { field: "urgency_level", as: "urgency_level", type: "string" },
          { field: "concierge_brief", as: "concierge_brief", type: "string" },
        ],
      ),

      // 7. VIP outreach sender (email)
      emailNode(
        "7",
        "Send VIP retention outreach email",
        1480,
        "{vip_email}",
        "{offer_subject}",
        "{offer_body}",
      ),

      // 8. Concierge call scheduler (Slack)
      slackNode(
        "8",
        "Alert concierge team to schedule VIP call",
        1720,
        ":rotating_light: *VIP Churn Alert - {risk_level} Risk*\\n\\n*Customer:* {vip_first_name} {vip_last_name} ({vip_company})\\n*Risk Score:* {churn_risk_score}/100\\n*Days Since Engagement:* {days_since_engagement}\\n*Lifetime Revenue:* ${lifetime_revenue}\\n*Offer Sent:* {offer_type} - {offer_value}\\n\\n*Concierge Brief:* {concierge_brief}\\n\\nPlease schedule a personal call within 48 hours.",
      ),

      // 9. Outcome logger (Sheets)
      sheetsNode(
        "9",
        "Log churn prevention outcome to Sheets",
        1960,
        "ChurnPrevention",
        '[["{assessment_date}","{vip_email}","{vip_first_name} {vip_last_name}","{vip_company}","{churn_risk_score}","{risk_level}","{offer_type}","{offer_value}","{days_since_engagement}","{lifetime_revenue}","outreach_sent"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1231. VIP CONCIERGE OUTREACH (7 nodes)
  // ===================================================================

  "VIP Concierge Outreach": {
    summary:
      "Listens for VIP milestone webhooks, extracts milestone data with AI, fetches deep customer profile from HubSpot, generates a personalized concierge brief with AI, routes outreach by preferred method via condition, sends a personal email to the VIP, and logs the interaction to Google Sheets.",
    nodes: [
      trigger("Webhook: VIP milestone event received", "webhook_app", {
        source: "crm",
        event: "vip.milestone.reached",
        howItStarts:
          "Fires when a VIP customer reaches a significant milestone such as account anniversary, spending threshold, usage landmark, or loyalty tier upgrade",
        setup:
          "Configure CRM webhook for VIP milestone events. Set OpenAI API key for concierge brief generation. Provide HubSpot API key for customer profile enrichment. Set Google Sheets spreadsheet ID for interaction logging.",
        inputFields: [],
      }),

      // 2. AI extract milestone data
      aiNode(
        "2",
        "AI: Extract VIP milestone details",
        280,
        "You are a data extraction assistant. Parse the raw webhook payload and extract VIP milestone information regardless of the payload format or field naming conventions. Return JSON with standardized fields.",
        "Extract VIP milestone data from this webhook payload: {webhook_payload}. Find the customer email, customer name, milestone type (anniversary, spending, usage, tier_upgrade), milestone value (e.g. 2 years, $50k spent, 1000 logins), milestone date, and any additional context regardless of how the fields are named. Return customer_email, customer_name, milestone_type, milestone_value, milestone_date, additional_context.",
        [
          { field: "customer_email", as: "vip_email", type: "string" },
          { field: "customer_name", as: "vip_name", type: "string" },
          { field: "milestone_type", as: "milestone_type", type: "string" },
          { field: "milestone_value", as: "milestone_value", type: "string" },
          { field: "milestone_date", as: "milestone_date", type: "string" },
          {
            field: "additional_context",
            as: "milestone_context",
            type: "string",
          },
        ],
      ),

      // 3. Customer profile deep dive (HubSpot)
      hubspotPostNode(
        "3",
        "Fetch VIP customer full profile",
        520,
        "crm/v3/objects/contacts/search",
        [
          {
            key: "filterGroups",
            value:
              '[{"filters":[{"propertyName":"email","operator":"EQ","value":"{vip_email}"}]}]',
          },
          {
            key: "properties",
            value:
              '["email","firstname","lastname","company","jobtitle","hs_analytics_revenue","hs_lifecyclestage_customer_date","phone","city"]',
          },
        ],
        [
          {
            name: "results[0].id",
            customName: "hubspot_contact_id",
            type: "string",
          },
          {
            name: "results[0].properties.company",
            customName: "vip_company",
            type: "string",
          },
          {
            name: "results[0].properties.jobtitle",
            customName: "vip_title",
            type: "string",
          },
          {
            name: "results[0].properties.hs_analytics_revenue",
            customName: "lifetime_revenue",
            type: "string",
          },
          {
            name: "results[0].properties.city",
            customName: "vip_city",
            type: "string",
          },
        ],
      ),

      // 4. AI concierge brief generator
      aiNode(
        "4",
        "AI: Generate concierge outreach brief",
        760,
        "You are a VIP concierge specialist. Create warm, personalized outreach messages that celebrate customer milestones. Be genuinely congratulatory without being over-the-top. Reference specific details about their journey. Return JSON.",
        "Create a concierge outreach for VIP: {vip_name} at {vip_company} ({vip_title}). Milestone: {milestone_type} - {milestone_value}. City: {vip_city}. Lifetime revenue: ${lifetime_revenue}. Context: {milestone_context}. Return email_subject (celebratory, personal), email_body (warm concierge letter referencing their journey and milestone), gift_recommendation (appropriate gift idea based on milestone), preferred_channel (email or phone based on milestone importance), followup_action (next concierge step).",
        [
          { field: "email_subject", as: "concierge_subject", type: "string" },
          { field: "email_body", as: "concierge_body", type: "string" },
          {
            field: "gift_recommendation",
            as: "gift_recommendation",
            type: "string",
          },
          {
            field: "preferred_channel",
            as: "preferred_channel",
            type: "string",
          },
          { field: "followup_action", as: "followup_action", type: "string" },
        ],
      ),

      // 5. Outreach method selector (condition)
      n("5", "condition", "Route by preferred channel: email vs phone", 1000, {
        conditionDetails: {
          conditions: [
            {
              variable: "{preferred_channel}",
              operator: "equals",
              value: "email",
            },
          ],
        },
      }),

      // 6. Personal email sender
      emailNode(
        "6",
        "Send personalized VIP concierge email",
        1240,
        "{vip_email}",
        "{concierge_subject}",
        "{concierge_body}",
      ),

      // 7. Interaction logger (Sheets)
      sheetsNode(
        "7",
        "Log VIP concierge interaction",
        1480,
        "VIPConcierge",
        '[["{milestone_date}","{vip_email}","{vip_name}","{vip_company}","{milestone_type}","{milestone_value}","{preferred_channel}","{gift_recommendation}","{followup_action}","{hubspot_contact_id}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1232. VIP EARLY ACCESS (8 nodes)
  // ===================================================================

  "VIP Early Access": {
    summary:
      "Launches from a manual trigger with early access event details, builds a VIP audience from HubSpot, generates exclusive messaging with AI, sends early access emails, dispatches SMS alerts via Twilio, manages access window timing with runCode, sends public launch transition emails, and tracks early access performance in Sheets.",
    nodes: [
      trigger("Manual: Early access event launched", "manual", {
        howItStarts:
          "A product or marketing manager initiates an early access event specifying the product name, access window duration, VIP tier eligibility, and exclusive benefits for early adopters",
        setup:
          "Configure HubSpot API key for VIP audience building. Set OpenAI API key for exclusive messaging. Provide Twilio Account SID and Auth Token for SMS alerts. Set Google Sheets spreadsheet ID for performance tracking.",
        inputFields: [
          {
            name: "product_name",
            type: "string",
            example: "AI Analytics Dashboard v2.0",
          },
          { name: "access_window_hours", type: "number", example: "48" },
          { name: "vip_tier_minimum", type: "string", example: "gold" },
          {
            name: "exclusive_benefit",
            type: "string",
            example: "30% lifetime discount + priority support",
          },
          {
            name: "launch_url",
            type: "string",
            example: "https://app.company.com/early-access",
          },
        ],
      }),

      // 2. HubSpot VIP audience builder
      hubspotPostNode(
        "2",
        "Build VIP early access audience",
        280,
        "crm/v3/objects/contacts/search",
        [
          {
            key: "filterGroups",
            value:
              '[{"filters":[{"propertyName":"hs_lead_status","operator":"EQ","value":"VIP"},{"propertyName":"lifecyclestage","operator":"EQ","value":"customer"}]}]',
          },
          {
            key: "properties",
            value:
              '["email","firstname","lastname","phone","company","hs_lead_status"]',
          },
          { key: "limit", value: "100" },
        ],
        [
          {
            name: "results[0].properties.email",
            customName: "vip_email",
            type: "string",
          },
          {
            name: "results[0].properties.firstname",
            customName: "vip_first_name",
            type: "string",
          },
          {
            name: "results[0].properties.phone",
            customName: "vip_phone",
            type: "string",
          },
          {
            name: "results[0].properties.company",
            customName: "vip_company",
            type: "string",
          },
          { name: "total", customName: "total_vip_audience", type: "number" },
        ],
      ),

      // 3. AI exclusive messaging generator
      aiNode(
        "3",
        "AI: Generate exclusive early access messaging",
        520,
        "You are a luxury brand copywriter. Create exclusive, excitement-building early access communications. Make VIPs feel genuinely special with sophisticated language. Never be salesy. Return JSON.",
        "Write exclusive early access communications for {product_name}. VIP: {vip_first_name} at {vip_company}. Benefit: {exclusive_benefit}. Access window: {access_window_hours} hours. Launch URL: {launch_url}. Total VIP audience: {total_vip_audience}. Return email_subject (exclusive urgency), email_body (luxury early access email), sms_message (concise SMS alert under 160 chars), countdown_text (urgency copy for the access window).",
        [
          { field: "email_subject", as: "ea_email_subject", type: "string" },
          { field: "email_body", as: "ea_email_body", type: "string" },
          { field: "sms_message", as: "ea_sms_message", type: "string" },
          { field: "countdown_text", as: "countdown_text", type: "string" },
        ],
      ),

      // 4. Early access email sender
      emailNode(
        "4",
        "Send VIP early access invitation email",
        760,
        "{vip_email}",
        "{ea_email_subject}",
        "{ea_email_body}",
      ),

      // 5. SMS alert (Twilio)
      smsNode(
        "5",
        "Send VIP early access SMS alert",
        1000,
        "{vip_phone}",
        "{ea_sms_message}",
      ),

      // 6. Access window controller (runCode)
      n("6", "runCode", "Calculate access window timing", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate access window start/end and transition timing
const windowHours = parseInt(vars.access_window_hours) || 48;
const startTime = new Date();
const endTime = new Date(startTime.getTime() + windowHours * 60 * 60 * 1000);
const halfwayTime = new Date(startTime.getTime() + (windowHours / 2) * 60 * 60 * 1000);
const totalVips = parseInt(vars.total_vip_audience) || 0;
return {
  window_start: startTime.toISOString(),
  window_end: endTime.toISOString(),
  halfway_reminder_time: halfwayTime.toISOString(),
  window_duration_display: windowHours + " hours",
  public_launch_date: endTime.toISOString().split("T")[0],
  audience_size: totalVips,
  event_id: "ea_" + Date.now().toString(36)
};`,
          outputVariables: [
            { name: "window_start", type: "string" },
            { name: "window_end", type: "string" },
            { name: "halfway_reminder_time", type: "string" },
            { name: "window_duration_display", type: "string" },
            { name: "public_launch_date", type: "string" },
            { name: "audience_size", type: "number" },
            { name: "event_id", type: "string" },
          ],
        },
      }),

      // 7. Public launch transition email
      emailNode(
        "7",
        "Send public launch transition notification",
        1480,
        "{vip_email}",
        "Your exclusive window is closing - {product_name} goes public {public_launch_date}",
        "Hi {vip_first_name},\\n\\nYour exclusive early access to {product_name} has been incredible. {countdown_text}\\n\\nThe public launch happens on {public_launch_date}. As a VIP, your {exclusive_benefit} is locked in permanently.\\n\\nAccess now: {launch_url}\\n\\nThank you for being one of our valued early adopters.\\n\\n{company_name}",
      ),

      // 8. Early access performance (Sheets)
      sheetsNode(
        "8",
        "Log early access performance metrics",
        1720,
        "EarlyAccess",
        '[["{window_start}","{event_id}","{product_name}","{vip_tier_minimum}","{audience_size}","{window_duration_display}","{exclusive_benefit}","{public_launch_date}","launched"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1233. VIP PRODUCT-DROP ALERT (8 nodes)
  // ===================================================================

  "VIP Product-Drop Alert": {
    summary:
      "Launches from a manual trigger with product drop details, builds VIP tier audience from HubSpot, composes drop alerts with AI, schedules tiered alerts with runCode, sends multi-channel email alerts, dispatches SMS via Twilio, monitors inventory levels via condition, and sends post-drop thank-you emails.",
    nodes: [
      trigger("Manual: Product drop announced", "manual", {
        howItStarts:
          "A merchandising or product manager initiates a product drop alert specifying the product name, drop time, inventory quantity, pricing, and VIP early-bird window",
        setup:
          "Configure HubSpot API key for VIP audience segmentation. Set OpenAI API key for alert copy generation. Provide Twilio credentials for SMS notifications. Set email service for multi-channel delivery.",
        inputFields: [
          {
            name: "drop_product_name",
            type: "string",
            example: "Limited Edition Pro Suite",
          },
          {
            name: "drop_time",
            type: "string",
            example: "2026-04-01T10:00:00Z",
          },
          { name: "total_inventory", type: "number", example: "500" },
          { name: "vip_reserved_pct", type: "number", example: "30" },
          { name: "drop_price", type: "string", example: "$299" },
          {
            name: "drop_url",
            type: "string",
            example: "https://shop.company.com/drops/pro-suite",
          },
        ],
      }),

      // 2. HubSpot VIP tier builder
      hubspotPostNode(
        "2",
        "Segment VIP customers by tier",
        280,
        "crm/v3/objects/contacts/search",
        [
          {
            key: "filterGroups",
            value:
              '[{"filters":[{"propertyName":"hs_lead_status","operator":"EQ","value":"VIP"}]}]',
          },
          {
            key: "properties",
            value:
              '["email","firstname","phone","company","hs_lead_status","hs_analytics_revenue"]',
          },
          { key: "limit", value: "100" },
        ],
        [
          {
            name: "results[0].properties.email",
            customName: "vip_email",
            type: "string",
          },
          {
            name: "results[0].properties.firstname",
            customName: "vip_first_name",
            type: "string",
          },
          {
            name: "results[0].properties.phone",
            customName: "vip_phone",
            type: "string",
          },
          {
            name: "results[0].properties.hs_analytics_revenue",
            customName: "vip_revenue",
            type: "string",
          },
          { name: "total", customName: "total_vips", type: "number" },
        ],
      ),

      // 3. AI drop alert composer
      aiNode(
        "3",
        "AI: Compose tiered product drop alerts",
        520,
        "You are a luxury product launch copywriter. Create excitement and urgency around exclusive product drops. Use scarcity and exclusivity language appropriately. Return JSON.",
        "Write product drop alerts for {drop_product_name} dropping at {drop_time}. Price: {drop_price}. Total inventory: {total_inventory}, VIP reserved: {vip_reserved_pct}%. VIP: {vip_first_name}, revenue: ${vip_revenue}. Drop URL: {drop_url}. Return alert_subject (urgent drop notification), alert_body (exclusive early access email with countdown), sms_alert (120 char SMS with urgency), vip_reserved_units (calculated from inventory and pct), scarcity_message (stock urgency copy).",
        [
          { field: "alert_subject", as: "drop_subject", type: "string" },
          { field: "alert_body", as: "drop_body", type: "string" },
          { field: "sms_alert", as: "drop_sms", type: "string" },
          {
            field: "vip_reserved_units",
            as: "vip_reserved_units",
            type: "string",
          },
          { field: "scarcity_message", as: "scarcity_message", type: "string" },
        ],
      ),

      // 4. Tiered alert scheduler (runCode)
      n("4", "runCode", "Calculate tiered alert schedule", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate tiered notification schedule for VIP drop
const dropTime = new Date(vars.drop_time || Date.now());
const now = new Date();
const inventory = parseInt(vars.total_inventory) || 500;
const vipPct = parseInt(vars.vip_reserved_pct) || 30;
const vipUnits = Math.floor(inventory * vipPct / 100);
const hoursUntilDrop = Math.max(0, (dropTime - now) / (1000 * 60 * 60));
const alertTier = hoursUntilDrop > 24 ? "advance" : hoursUntilDrop > 2 ? "imminent" : "live";
return {
  alert_tier: alertTier,
  hours_until_drop: Math.round(hoursUntilDrop),
  vip_allocation: vipUnits,
  general_allocation: inventory - vipUnits,
  drop_id: "drop_" + Date.now().toString(36),
  alert_timestamp: now.toISOString(),
  inventory_remaining: "yes"
};`,
          outputVariables: [
            { name: "alert_tier", type: "string" },
            { name: "hours_until_drop", type: "number" },
            { name: "vip_allocation", type: "number" },
            { name: "general_allocation", type: "number" },
            { name: "drop_id", type: "string" },
            { name: "alert_timestamp", type: "string" },
            { name: "inventory_remaining", type: "string" },
          ],
        },
      }),

      // 5. Multi-channel alert email sender
      emailNode(
        "5",
        "Send VIP product drop alert email",
        1000,
        "{vip_email}",
        "{drop_subject}",
        "{drop_body}",
      ),

      // 6. SMS alert (Twilio)
      smsNode(
        "6",
        "Send VIP drop SMS notification",
        1240,
        "{vip_phone}",
        "{drop_sms}",
      ),

      // 7. Inventory monitor (condition)
      n("7", "condition", "Check inventory: VIP allocation remaining", 1480, {
        conditionDetails: {
          conditions: [
            {
              variable: "{inventory_remaining}",
              operator: "equals",
              value: "yes",
            },
          ],
        },
      }),

      // 8. Post-drop thank-you email
      emailNode(
        "8",
        "Send post-drop VIP thank-you email",
        1720,
        "{vip_email}",
        "Thank you for being first, {vip_first_name} - {drop_product_name}",
        "Hi {vip_first_name},\\n\\nThank you for being part of our exclusive {drop_product_name} drop!\\n\\n{scarcity_message}\\n\\nYour VIP allocation of {vip_allocation} units was reserved just for customers like you. We had {total_vips} VIPs notified {hours_until_drop} hours before the public.\\n\\nDrop link: {drop_url}\\n\\nAs always, you get first access to everything we release.\\n\\nWith appreciation,\\n{company_name}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
  },

  // ===================================================================
  // 1234. VIP OR NEW LOYALTY MEMBER WELCOME (8 nodes)
  // ===================================================================

  "VIP or New Loyalty Member Welcome": {
    summary:
      "Listens for loyalty enrollment webhooks, extracts member data with AI, identifies member tier from HubSpot, selects tier-specific content via condition, generates a personalized welcome with AI, sends the welcome email, prompts loyalty app download via email, and logs the enrollment to HubSpot CRM.",
    nodes: [
      trigger("Webhook: Loyalty enrollment event", "webhook_app", {
        source: "loyalty_platform",
        event: "member.enrolled",
        howItStarts:
          "Fires when a new customer enrolls in the loyalty program or an existing member achieves VIP tier status, capturing their enrollment details and tier information",
        setup:
          "Configure loyalty platform webhook for enrollment events. Set OpenAI API key for welcome content generation. Provide HubSpot API key for CRM tier lookup and logging. Set email service credentials for welcome sequence.",
        inputFields: [],
      }),

      // 2. AI extract member data
      aiNode(
        "2",
        "AI: Extract loyalty member enrollment data",
        280,
        "You are a data extraction assistant. Parse the raw webhook payload and extract loyalty member enrollment information regardless of format variations or field naming conventions. Return JSON.",
        "Extract loyalty enrollment data from this webhook payload: {webhook_payload}. Find the member email, member name, enrollment tier (silver, gold, platinum, VIP), initial points balance, enrollment date, referral source if present, and whether this is a new enrollment or tier upgrade. Return member_email, member_name, enrollment_tier, points_balance, enrollment_date, referral_source, is_tier_upgrade.",
        [
          { field: "member_email", as: "member_email", type: "string" },
          { field: "member_name", as: "member_name", type: "string" },
          { field: "enrollment_tier", as: "enrollment_tier", type: "string" },
          { field: "points_balance", as: "points_balance", type: "string" },
          { field: "enrollment_date", as: "enrollment_date", type: "string" },
          { field: "referral_source", as: "referral_source", type: "string" },
          { field: "is_tier_upgrade", as: "is_tier_upgrade", type: "string" },
        ],
      ),

      // 3. Member tier identifier (HubSpot)
      hubspotPostNode(
        "3",
        "Lookup member profile in HubSpot",
        520,
        "crm/v3/objects/contacts/search",
        [
          {
            key: "filterGroups",
            value:
              '[{"filters":[{"propertyName":"email","operator":"EQ","value":"{member_email}"}]}]',
          },
          {
            key: "properties",
            value:
              '["email","firstname","lastname","company","lifecyclestage","hs_analytics_revenue","num_associated_deals"]',
          },
        ],
        [
          { name: "results[0].id", customName: "hubspot_id", type: "string" },
          {
            name: "results[0].properties.company",
            customName: "member_company",
            type: "string",
          },
          {
            name: "results[0].properties.hs_analytics_revenue",
            customName: "member_revenue",
            type: "string",
          },
          {
            name: "results[0].properties.num_associated_deals",
            customName: "member_deals",
            type: "string",
          },
          { name: "total", customName: "existing_contacts", type: "number" },
        ],
      ),

      // 4. Tier-specific content selector (condition)
      n("4", "condition", "Route by tier: VIP/platinum vs standard", 760, {
        conditionDetails: {
          conditions: [
            {
              variable: "{enrollment_tier}",
              operator: "contains",
              value: "vip",
            },
          ],
        },
      }),

      // 5. AI welcome composer
      aiNode(
        "5",
        "AI: Generate tier-appropriate welcome content",
        1000,
        "You are a loyalty program welcome specialist. Create warm, exciting welcome messages that make new members feel valued and motivated to engage. Tailor tone and benefits to the member tier. Return JSON.",
        "Write a loyalty welcome for {member_name} ({member_email}). Tier: {enrollment_tier}. Points: {points_balance}. Company: {member_company}. Revenue: ${member_revenue}. Tier upgrade: {is_tier_upgrade}. Referral: {referral_source}. Return welcome_subject (exciting welcome), welcome_body (personalized welcome email with tier benefits, points info, and next-step CTA), app_download_cta (text encouraging app download), tier_benefits_summary (3-4 bullet points of tier perks).",
        [
          { field: "welcome_subject", as: "welcome_subject", type: "string" },
          { field: "welcome_body", as: "welcome_body", type: "string" },
          { field: "app_download_cta", as: "app_download_cta", type: "string" },
          {
            field: "tier_benefits_summary",
            as: "tier_benefits",
            type: "string",
          },
        ],
      ),

      // 6. Welcome email sender
      emailNode(
        "6",
        "Send loyalty welcome email",
        1240,
        "{member_email}",
        "{welcome_subject}",
        "{welcome_body}",
      ),

      // 7. Loyalty app prompt email
      emailNode(
        "7",
        "Send loyalty app download prompt",
        1480,
        "{member_email}",
        "Download the app to track your {enrollment_tier} rewards, {member_name}",
        "Hi {member_name},\\n\\nWelcome to the {enrollment_tier} tier! {app_download_cta}\\n\\nYour current perks:\\n{tier_benefits}\\n\\nYour points balance: {points_balance}\\n\\nDownload our loyalty app to track rewards, earn bonus points, and unlock exclusive offers:\\n- iOS: https://apps.apple.com/app/{app_id}\\n- Android: https://play.google.com/store/apps/details?id={app_id}\\n\\nHappy earning!\\n{company_name}",
      ),

      // 8. CRM loyalty logger (HubSpot)
      hubspotPostNode(
        "8",
        "Update HubSpot with loyalty enrollment",
        1720,
        "crm/v3/objects/contacts/{hubspot_id}",
        [
          {
            key: "properties",
            value:
              '{"hs_lead_status":"{enrollment_tier}","loyalty_tier":"{enrollment_tier}","loyalty_points":"{points_balance}","loyalty_enrolled_date":"{enrollment_date}"}',
          },
        ],
        [{ name: "id", customName: "updated_contact_id", type: "string" }],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1235. VIDEO ASSET LIBRARY BUILDER (9 nodes)
  // ===================================================================

  "Video Asset Library Builder": {
    summary:
      "Launches from a manual trigger with a product catalog reference, enriches product data via Shopify, generates multi-angle video scripts with AI, queues batch rendering with runCode, renders videos through Kling AI, validates quality via condition, organizes assets in Sheets, tags metadata with runCode, and reports library completion to Slack.",
    nodes: [
      trigger("Manual: Product catalog video generation", "manual", {
        howItStarts:
          "A content manager submits a product catalog reference to generate a comprehensive video asset library with multiple angles and formats for each product",
        setup:
          "Configure Shopify API credentials for product data. Set OpenAI API key for script generation. Provide Kling AI API key for video rendering. Set Google Sheets spreadsheet ID for asset library. Configure Slack webhook for completion alerts.",
        inputFields: [
          { name: "product_id", type: "string", example: "8234567890" },
          {
            name: "video_style",
            type: "string",
            example: "modern, clean, product-focused",
          },
          {
            name: "target_platforms",
            type: "string",
            example: "TikTok, Instagram Reels, YouTube Shorts",
          },
          {
            name: "brand_colors",
            type: "string",
            example: "#1A73E8, #FFFFFF, #333333",
          },
        ],
      }),

      // 2. Shopify product data enricher
      n("2", "hitApi", "Fetch product details from Shopify", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://{shopify_store}.myshopify.com/admin/api/2024-01/products/{product_id}.json",
          headers: [
            { key: "X-Shopify-Access-Token", value: "{shopify_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "product.title",
              customName: "product_title",
              type: "string",
            },
            {
              name: "product.body_html",
              customName: "product_description",
              type: "string",
            },
            {
              name: "product.images[0].src",
              customName: "product_image_url",
              type: "string",
            },
            {
              name: "product.variants[0].price",
              customName: "product_price",
              type: "string",
            },
            {
              name: "product.product_type",
              customName: "product_type",
              type: "string",
            },
            {
              name: "product.tags",
              customName: "product_tags",
              type: "string",
            },
          ],
        },
      }),

      // 3. AI multi-angle script generator
      aiNode(
        "3",
        "AI: Generate multi-angle video scripts",
        520,
        "You are a video production specialist. Create detailed video scripts for multiple angles and formats of the same product. Each script should be optimized for its target platform. Return JSON.",
        "Generate video scripts for product: {product_title}. Description: {product_description}. Price: ${product_price}. Type: {product_type}. Style: {video_style}. Platforms: {target_platforms}. Brand colors: {brand_colors}. Return hero_script (30s hero product video), demo_script (15s feature demonstration), lifestyle_script (20s lifestyle context), unboxing_script (25s unboxing experience), total_scripts (number of scripts generated), estimated_render_minutes (time estimate).",
        [
          { field: "hero_script", as: "hero_script", type: "string" },
          { field: "demo_script", as: "demo_script", type: "string" },
          { field: "lifestyle_script", as: "lifestyle_script", type: "string" },
          { field: "unboxing_script", as: "unboxing_script", type: "string" },
          { field: "total_scripts", as: "total_scripts", type: "number" },
          {
            field: "estimated_render_minutes",
            as: "est_render_min",
            type: "string",
          },
        ],
      ),

      // 4. Batch render queue (runCode)
      n("4", "runCode", "Prepare batch render queue", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Prepare batch rendering queue for all video scripts
const batchId = "batch_" + Date.now().toString(36);
const scripts = [
  { name: "hero", script: vars.hero_script, duration: 30 },
  { name: "demo", script: vars.demo_script, duration: 15 },
  { name: "lifestyle", script: vars.lifestyle_script, duration: 20 },
  { name: "unboxing", script: vars.unboxing_script, duration: 25 }
].filter(s => s.script);
const totalDuration = scripts.reduce((sum, s) => sum + s.duration, 0);
return {
  batch_id: batchId,
  render_queue_json: JSON.stringify(scripts),
  total_videos: scripts.length,
  total_duration_seconds: totalDuration,
  primary_render_script: scripts[0] ? scripts[0].script : "",
  render_status: "queued"
};`,
          outputVariables: [
            { name: "batch_id", type: "string" },
            { name: "render_queue_json", type: "string" },
            { name: "total_videos", type: "number" },
            { name: "total_duration_seconds", type: "number" },
            { name: "primary_render_script", type: "string" },
            { name: "render_status", type: "string" },
          ],
        },
      }),

      // 5. Kling batch renderer API
      n("5", "hitApi", "Render hero video via Kling AI", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.klingai.com/v1/videos/text2video",
          headers: [
            { key: "Authorization", value: "Bearer {kling_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "prompt", value: "{primary_render_script}" },
            { key: "duration", value: "30" },
            { key: "aspect_ratio", value: "9:16" },
            { key: "model", value: "kling-v1" },
            { key: "ref_image", value: "{product_image_url}" },
          ],
          variables: [
            {
              name: "data.task_id",
              customName: "render_task_id",
              type: "string",
            },
            {
              name: "data.video_url",
              customName: "hero_video_url",
              type: "string",
            },
            {
              name: "data.status",
              customName: "render_result_status",
              type: "string",
            },
          ],
        },
      }),

      // 6. Quality assurance validator (condition)
      n("6", "condition", "Check render quality: status is success", 1240, {
        conditionDetails: {
          conditions: [
            {
              variable: "{render_result_status}",
              operator: "equals",
              value: "success",
            },
          ],
        },
      }),

      // 7. Asset organizer (Sheets)
      sheetsNode(
        "7",
        "Organize video assets in library",
        1480,
        "VideoAssets",
        '[["{batch_id}","{product_title}","{product_type}","hero","{hero_video_url}","{render_task_id}","{total_videos}","{total_duration_seconds}","{video_style}","{target_platforms}","approved"]]',
      ),

      // 8. Metadata tagger (runCode)
      n("8", "runCode", "Generate asset metadata tags", 1720, {
        runCodeDetails: {
          language: "javascript",
          code: `// Generate metadata tags for the video asset library
const productTitle = vars.product_title || "Unknown";
const productType = vars.product_type || "General";
const tags = vars.product_tags || "";
const platforms = vars.target_platforms || "";
const style = vars.video_style || "";
const autoTags = [productType.toLowerCase(), style, "product-video", "ai-generated"];
const existingTags = tags.split(",").map(t => t.trim()).filter(Boolean);
const allTags = [...new Set([...autoTags, ...existingTags])].join(", ");
const platformList = platforms.split(",").map(p => p.trim()).filter(Boolean);
return {
  metadata_tags: allTags,
  platform_count: platformList.length,
  asset_naming_convention: productTitle.replace(/[^a-zA-Z0-9]/g, "_").toLowerCase() + "_" + vars.batch_id,
  library_category: productType,
  completion_timestamp: new Date().toISOString()
};`,
          outputVariables: [
            { name: "metadata_tags", type: "string" },
            { name: "platform_count", type: "number" },
            { name: "asset_naming_convention", type: "string" },
            { name: "library_category", type: "string" },
            { name: "completion_timestamp", type: "string" },
          ],
        },
      }),

      // 9. Library completion reporter (Slack)
      slackNode(
        "9",
        "Notify team: video asset library complete",
        1960,
        ":movie_camera: *Video Asset Library Complete*\\n\\n*Product:* {product_title} ({product_type})\\n*Batch:* {batch_id}\\n*Videos rendered:* {total_videos} ({total_duration_seconds}s total)\\n*Platforms:* {target_platforms}\\n*Tags:* {metadata_tags}\\n*Asset name:* {asset_naming_convention}\\n*Hero video:* {hero_video_url}\\n\\nAll assets organized in the library sheet.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1236. VIDEO MULTI-PLATFORM DISTRIBUTOR (8 nodes)
  // ===================================================================

  "Video Multi-Platform Distributor": {
    summary:
      "Launches from a manual trigger with a source video, analyzes platform requirements with runCode, generates captions with AI, validates video format via condition, gates on human approval, publishes to TikTok via API, publishes to Instagram via API, and tracks cross-platform performance in Sheets.",
    nodes: [
      trigger("Manual: Source video ready for distribution", "manual", {
        howItStarts:
          "A content creator submits a completed video for multi-platform distribution, specifying the source video URL, title, description, target platforms, and any overrides",
        setup:
          "Configure OpenAI API key for caption generation. Provide TikTok Marketing API credentials. Set Instagram Graph API and LinkedIn Marketing API tokens. Set Google Sheets spreadsheet ID for performance tracking.",
        inputFields: [
          {
            name: "video_url",
            type: "string",
            example: "https://storage.company.com/videos/product-demo-v3.mp4",
          },
          {
            name: "video_title",
            type: "string",
            example: "5 Features That Changed Everything",
          },
          {
            name: "video_description",
            type: "string",
            example: "Our top 5 features and how they transform your workflow",
          },
          { name: "video_duration_seconds", type: "number", example: "60" },
          {
            name: "target_platforms",
            type: "string",
            example: "TikTok, Instagram Reels, LinkedIn",
          },
        ],
      }),

      // 2. Platform requirements analyzer (runCode)
      n("2", "runCode", "Analyze platform format requirements", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Analyze video compatibility with each platform
const duration = parseInt(vars.video_duration_seconds) || 60;
const platforms = (vars.target_platforms || "").split(",").map(p => p.trim());
const tiktokOk = duration <= 180;
const reelsOk = duration <= 90;
const linkedinOk = duration <= 600;
const allFormatsValid = tiktokOk && reelsOk && linkedinOk;
const issues = [];
if (!tiktokOk) issues.push("TikTok max 180s");
if (!reelsOk) issues.push("Reels max 90s");
if (!linkedinOk) issues.push("LinkedIn max 600s");
return {
  platform_count: platforms.length,
  tiktok_compatible: tiktokOk ? "yes" : "no",
  reels_compatible: reelsOk ? "yes" : "no",
  linkedin_compatible: linkedinOk ? "yes" : "no",
  all_formats_valid: allFormatsValid ? "yes" : "no",
  format_issues: issues.length > 0 ? issues.join("; ") : "none",
  distribution_id: "dist_" + Date.now().toString(36)
};`,
          outputVariables: [
            { name: "platform_count", type: "number" },
            { name: "tiktok_compatible", type: "string" },
            { name: "reels_compatible", type: "string" },
            { name: "linkedin_compatible", type: "string" },
            { name: "all_formats_valid", type: "string" },
            { name: "format_issues", type: "string" },
            { name: "distribution_id", type: "string" },
          ],
        },
      }),

      // 3. AI caption generator
      aiNode(
        "3",
        "AI: Generate platform-optimized captions",
        520,
        "You are a social media caption specialist. Write platform-native captions that maximize engagement on each platform. Use appropriate tone, length, hashtags, and CTAs for each. Return JSON.",
        "Generate captions for video: {video_title}. Description: {video_description}. Duration: {video_duration_seconds}s. Platforms: {target_platforms}. Return tiktok_caption (fun, trendy, hashtags, max 300 chars), instagram_caption (engaging, with relevant hashtags, max 500 chars), linkedin_caption (professional, value-driven, max 700 chars), universal_hashtags (5 relevant hashtags).",
        [
          { field: "tiktok_caption", as: "tiktok_caption", type: "string" },
          {
            field: "instagram_caption",
            as: "instagram_caption",
            type: "string",
          },
          { field: "linkedin_caption", as: "linkedin_caption", type: "string" },
          {
            field: "universal_hashtags",
            as: "universal_hashtags",
            type: "string",
          },
        ],
      ),

      // 4. Video format validator (condition)
      n("4", "condition", "Check format: all platforms compatible", 760, {
        conditionDetails: {
          conditions: [
            {
              variable: "{all_formats_valid}",
              operator: "equals",
              value: "yes",
            },
          ],
        },
      }),

      // 5. Human approval gate
      n("5", "approvalGate", "Content Approval Required", 1000, {
        approvalGateDetails: {
          approvers: ["content-lead@company.com"],
          message:
            "Video ready for multi-platform distribution:\\n\\nTitle: {video_title}\\nDuration: {video_duration_seconds}s\\nPlatforms: {target_platforms}\\nFormat issues: {format_issues}\\n\\nTikTok: {tiktok_caption}\\nInstagram: {instagram_caption}\\nLinkedIn: {linkedin_caption}\\n\\nApprove to publish across all platforms.",
          timeoutHours: 24,
        },
      }),

      // 6. TikTok publisher API
      n("6", "hitApi", "Publish video to TikTok", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://open.tiktokapis.com/v2/post/publish/video/init/",
          headers: [
            { key: "Authorization", value: "Bearer {tiktok_access_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "post_info",
              value:
                '{"title":"{video_title}","description":"{tiktok_caption}","disable_duet":false,"disable_stitch":false,"privacy_level":"PUBLIC_TO_EVERYONE"}',
            },
            {
              key: "source_info",
              value: '{"source":"PULL_FROM_URL","video_url":"{video_url}"}',
            },
          ],
          variables: [
            {
              name: "data.publish_id",
              customName: "tiktok_publish_id",
              type: "string",
            },
          ],
        },
      }),

      // 7. Instagram publisher API
      n("7", "hitApi", "Publish Reel to Instagram", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://graph.facebook.com/v18.0/{ig_business_id}/media",
          headers: [
            { key: "Authorization", value: "Bearer {meta_access_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "media_type", value: "REELS" },
            { key: "video_url", value: "{video_url}" },
            { key: "caption", value: "{instagram_caption}" },
          ],
          variables: [
            { name: "id", customName: "instagram_media_id", type: "string" },
          ],
        },
      }),

      // 8. Cross-platform performance (Sheets)
      sheetsNode(
        "8",
        "Log cross-platform distribution",
        1720,
        "VideoDistribution",
        '[["{distribution_id}","{video_title}","{video_duration_seconds}","{platform_count}","{tiktok_publish_id}","{instagram_media_id}","{tiktok_caption}","{universal_hashtags}","{format_issues}","published"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1237. VIRAL CONTENT DETECTOR (8 nodes)
  // ===================================================================

  "Viral Content Detector": {
    summary:
      "Runs hourly to collect performance metrics from Meta API, calculates velocity and engagement rates with runCode, detects viral threshold breaches via condition, analyzes trending patterns with AI, sends immediate Slack alerts, recommends amplification strategies via email, and logs trend data to Sheets.",
    nodes: [
      trigger("Schedule: Hourly viral content scan", "schedule", {
        schedule: "0 * * * * (Every hour)",
        howItStarts:
          "An hourly schedule checks all recent social media posts for viral velocity indicators including engagement rate spikes, share velocity, comment sentiment shifts, and follower growth anomalies",
        setup:
          "Configure Meta Graph API token with page insights access. Set OpenAI API key for trend analysis. Provide Slack webhook for immediate viral alerts. Set Google Sheets spreadsheet ID for trend database.",
        inputFields: [],
      }),

      // 2. Meta API performance metrics collector
      n("2", "hitApi", "Fetch recent post performance from Meta", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://graph.facebook.com/v18.0/{page_id}/posts?fields=id,message,created_time,shares,likes.summary(true),comments.summary(true)&limit=10",
          headers: [
            { key: "Authorization", value: "Bearer {meta_access_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "data[0].id", customName: "top_post_id", type: "string" },
            {
              name: "data[0].message",
              customName: "top_post_message",
              type: "string",
            },
            {
              name: "data[0].likes.summary.total_count",
              customName: "top_likes",
              type: "number",
            },
            {
              name: "data[0].comments.summary.total_count",
              customName: "top_comments",
              type: "number",
            },
            {
              name: "data[0].shares.count",
              customName: "top_shares",
              type: "number",
            },
            {
              name: "data[0].created_time",
              customName: "top_post_created",
              type: "string",
            },
            {
              name: "data.length",
              customName: "posts_analyzed",
              type: "number",
            },
          ],
        },
      }),

      // 3. Velocity calculator (runCode)
      n("3", "runCode", "Calculate engagement velocity metrics", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate viral velocity metrics
const likes = parseInt(vars.top_likes) || 0;
const comments = parseInt(vars.top_comments) || 0;
const shares = parseInt(vars.top_shares) || 0;
const created = new Date(vars.top_post_created || Date.now());
const hoursLive = Math.max(1, (Date.now() - created.getTime()) / (1000 * 60 * 60));
const totalEngagements = likes + comments + shares;
const engagementVelocity = Math.round(totalEngagements / hoursLive);
const shareRatio = totalEngagements > 0 ? (shares / totalEngagements * 100).toFixed(1) : "0";
const viralScore = (engagementVelocity * 2) + (shares * 5) + (comments * 3);
const isViral = viralScore >= 500 ? "yes" : "no";
const viralTier = viralScore >= 2000 ? "mega_viral" : viralScore >= 1000 ? "viral" : viralScore >= 500 ? "trending" : "normal";
return {
  engagement_velocity: engagementVelocity,
  total_engagements: totalEngagements,
  share_ratio_pct: shareRatio,
  viral_score: viralScore,
  is_viral: isViral,
  viral_tier: viralTier,
  hours_live: Math.round(hoursLive),
  scan_timestamp: new Date().toISOString()
};`,
          outputVariables: [
            { name: "engagement_velocity", type: "number" },
            { name: "total_engagements", type: "number" },
            { name: "share_ratio_pct", type: "string" },
            { name: "viral_score", type: "number" },
            { name: "is_viral", type: "string" },
            { name: "viral_tier", type: "string" },
            { name: "hours_live", type: "number" },
            { name: "scan_timestamp", type: "string" },
          ],
        },
      }),

      // 4. Viral threshold detector (condition)
      n("4", "condition", "Check viral threshold: score >= 500", 760, {
        conditionDetails: {
          conditions: [
            { variable: "{is_viral}", operator: "equals", value: "yes" },
          ],
        },
      }),

      // 5. AI trend analyzer
      aiNode(
        "5",
        "AI: Analyze viral trend patterns",
        1000,
        "You are a social media analytics expert specializing in viral content patterns. Analyze why content is going viral and provide actionable insights. Return JSON.",
        "Analyze this viral content. Post: {top_post_message}. Metrics: {total_engagements} engagements in {hours_live} hours. Velocity: {engagement_velocity}/hr. Shares: {top_shares} ({share_ratio_pct}% share ratio). Viral score: {viral_score} ({viral_tier}). Posts analyzed: {posts_analyzed}. Return trend_analysis (why this is resonating), audience_insight (who is engaging and why), amplification_strategy (how to maximize reach now), content_replication_tips (how to create more like this), peak_prediction (expected peak timing).",
        [
          { field: "trend_analysis", as: "trend_analysis", type: "string" },
          { field: "audience_insight", as: "audience_insight", type: "string" },
          {
            field: "amplification_strategy",
            as: "amplification_strategy",
            type: "string",
          },
          {
            field: "content_replication_tips",
            as: "replication_tips",
            type: "string",
          },
          { field: "peak_prediction", as: "peak_prediction", type: "string" },
        ],
      ),

      // 6. Immediate alert (Slack)
      slackNode(
        "6",
        "Send viral content alert to Slack",
        1240,
        ":fire: *VIRAL CONTENT DETECTED - {viral_tier}*\\n\\n*Post:* {top_post_message}\\n*Viral Score:* {viral_score} | *Velocity:* {engagement_velocity}/hr\\n*Engagements:* {total_engagements} in {hours_live}hrs\\n*Likes:* {top_likes} | *Comments:* {top_comments} | *Shares:* {top_shares}\\n\\n*Analysis:* {trend_analysis}\\n*Amplification:* {amplification_strategy}\\n*Peak Predicted:* {peak_prediction}",
      ),

      // 7. Amplification recommender (email)
      emailNode(
        "7",
        "Send amplification strategy to marketing team",
        1480,
        "{marketing_team_email}",
        "VIRAL ALERT: {viral_tier} content detected - score {viral_score}",
        "VIRAL CONTENT ALERT\\n\\nPost: {top_post_message}\\nPost ID: {top_post_id}\\n\\nMetrics:\\n- Engagement velocity: {engagement_velocity}/hour\\n- Total engagements: {total_engagements}\\n- Share ratio: {share_ratio_pct}%\\n- Hours live: {hours_live}\\n\\nTrend Analysis: {trend_analysis}\\n\\nAudience Insight: {audience_insight}\\n\\nAmplification Strategy: {amplification_strategy}\\n\\nReplication Tips: {replication_tips}\\n\\nPeak Prediction: {peak_prediction}\\n\\nAct quickly to maximize this viral moment.",
      ),

      // 8. Trend database (Sheets)
      sheetsNode(
        "8",
        "Log viral trend to database",
        1720,
        "ViralTrends",
        '[["{scan_timestamp}","{top_post_id}","{viral_tier}","{viral_score}","{engagement_velocity}","{total_engagements}","{top_likes}","{top_comments}","{top_shares}","{share_ratio_pct}","{hours_live}","{trend_analysis}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1238. WEBENGAGE CROSS-CHANNEL ORCHESTRATION (8 nodes)
  // ===================================================================

  "WebEngage Cross-Channel Orchestration": {
    summary:
      "Listens for customer event webhooks, extracts event data with AI, fetches user profile from HubSpot, classifies journey stage with runCode, ranks channel priority via condition, sends primary channel email, waits for engagement via delay, and sends fallback channel SMS if needed.",
    nodes: [
      trigger("Webhook: Customer engagement event", "webhook_app", {
        source: "webengage",
        event: "customer.event",
        howItStarts:
          "Fires when WebEngage captures a customer event such as page view, cart action, purchase, or feature usage, triggering cross-channel orchestration based on event type and user profile",
        setup:
          "Configure WebEngage webhook for customer events. Set OpenAI API key for event extraction. Provide HubSpot API key for user profile lookup. Configure email and SMS service credentials for multi-channel delivery.",
        inputFields: [],
      }),

      // 2. AI extract event data
      aiNode(
        "2",
        "AI: Extract customer event details",
        280,
        "You are a data extraction assistant for customer engagement events. Parse raw webhook payloads from engagement platforms and extract standardized event data regardless of field naming format. Return JSON.",
        "Extract customer event data from this webhook payload: {webhook_payload}. Find the user email, user ID, event name (page_view, add_to_cart, purchase, feature_used, etc.), event timestamp, event properties (product ID, page URL, amount, etc.), device type, and session ID regardless of how the fields are named. Return user_email, user_id, event_name, event_timestamp, event_properties, device_type, session_id.",
        [
          { field: "user_email", as: "user_email", type: "string" },
          { field: "user_id", as: "user_id", type: "string" },
          { field: "event_name", as: "event_name", type: "string" },
          { field: "event_timestamp", as: "event_timestamp", type: "string" },
          { field: "event_properties", as: "event_properties", type: "string" },
          { field: "device_type", as: "device_type", type: "string" },
          { field: "session_id", as: "session_id", type: "string" },
        ],
      ),

      // 3. User profile fetcher (HubSpot)
      hubspotPostNode(
        "3",
        "Fetch user profile from HubSpot",
        520,
        "crm/v3/objects/contacts/search",
        [
          {
            key: "filterGroups",
            value:
              '[{"filters":[{"propertyName":"email","operator":"EQ","value":"{user_email}"}]}]',
          },
          {
            key: "properties",
            value:
              '["email","firstname","lastname","phone","lifecyclestage","hs_email_last_open_date","hs_analytics_num_page_views","num_associated_deals"]',
          },
        ],
        [
          {
            name: "results[0].properties.firstname",
            customName: "user_first_name",
            type: "string",
          },
          {
            name: "results[0].properties.phone",
            customName: "user_phone",
            type: "string",
          },
          {
            name: "results[0].properties.lifecyclestage",
            customName: "lifecycle_stage",
            type: "string",
          },
          {
            name: "results[0].properties.hs_email_last_open_date",
            customName: "last_email_open",
            type: "string",
          },
          {
            name: "results[0].properties.hs_analytics_num_page_views",
            customName: "page_views",
            type: "string",
          },
        ],
      ),

      // 4. Journey stage classifier (runCode)
      n("4", "runCode", "Classify journey stage and channel priority", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Classify user journey stage and determine channel priority
const stage = (vars.lifecycle_stage || "subscriber").toLowerCase();
const event = (vars.event_name || "").toLowerCase();
const lastOpen = vars.last_email_open || "";
const pageViews = parseInt(vars.page_views) || 0;
let journeyPhase = "awareness";
if (event.includes("purchase") || stage === "customer") journeyPhase = "retention";
else if (event.includes("cart") || stage === "opportunity") journeyPhase = "decision";
else if (pageViews > 5 || stage === "lead") journeyPhase = "consideration";
const emailRecent = lastOpen && new Date(lastOpen) > new Date(Date.now() - 7*24*60*60*1000);
const primaryChannel = emailRecent ? "email" : "sms";
return {
  journey_phase: journeyPhase,
  primary_channel: primaryChannel,
  email_is_primary: primaryChannel === "email" ? "yes" : "no",
  engagement_level: pageViews > 10 ? "high" : pageViews > 3 ? "medium" : "low",
  orchestration_id: "orch_" + Date.now().toString(36)
};`,
          outputVariables: [
            { name: "journey_phase", type: "string" },
            { name: "primary_channel", type: "string" },
            { name: "email_is_primary", type: "string" },
            { name: "engagement_level", type: "string" },
            { name: "orchestration_id", type: "string" },
          ],
        },
      }),

      // 5. Channel priority ranker (condition)
      n("5", "condition", "Route by primary channel: email preferred", 1000, {
        conditionDetails: {
          conditions: [
            {
              variable: "{email_is_primary}",
              operator: "equals",
              value: "yes",
            },
          ],
        },
      }),

      // 6. Primary channel sender (email)
      emailNode(
        "6",
        "Send primary channel email message",
        1240,
        "{user_email}",
        "We noticed your activity, {user_first_name} - here is what comes next",
        "Hi {user_first_name},\\n\\nWe noticed your recent {event_name} activity and wanted to make sure you have everything you need.\\n\\nBased on your {journey_phase} journey phase, here are some helpful next steps:\\n\\n- Explore related features and content\\n- Connect with our team for personalized guidance\\n- Check out what other {engagement_level}-engagement users like you are doing\\n\\nEvent details: {event_properties}\\n\\nWe are here to help whenever you need us.\\n\\n{company_name}",
      ),

      // 7. Fallback wait (delay)
      n("7", "delayAction", "Wait 4 hours for email engagement", 1480, {
        delayDetails: { duration: 4, unit: "hours" },
      }),

      // 8. Fallback channel sender (SMS)
      smsNode(
        "8",
        "Send fallback SMS notification",
        1720,
        "{user_phone}",
        "Hi {user_first_name}, we noticed your {event_name} activity. Check your email for personalized next steps, or reply HELP to connect with our team. - {company_name}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "8", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1239. WEBENGAGE JOURNEY BUILDER AUTOMATION (9 nodes)
  // ===================================================================

  "WebEngage Journey Builder Automation": {
    summary:
      "Listens for journey entry webhooks, extracts user data with AI, enriches user attributes from HubSpot, applies decision branching via condition, waits for optimal engagement timing with delay, sends multi-channel email, checks engagement via condition, runs A/B split tests with runCode, and logs journey performance to Sheets.",
    nodes: [
      trigger("Webhook: Journey entry event received", "webhook_app", {
        source: "webengage",
        event: "journey.user_entered",
        howItStarts:
          "Fires when a user enters a WebEngage journey based on segment membership, event trigger, or manual import, capturing their entry context and initial attributes",
        setup:
          "Configure WebEngage webhook for journey entry events. Set OpenAI API key for data extraction. Provide HubSpot API key for attribute enrichment. Set Google Sheets spreadsheet ID for journey analytics.",
        inputFields: [],
      }),

      // 2. AI extract user data
      aiNode(
        "2",
        "AI: Extract journey entry user data",
        280,
        "You are a data extraction assistant for marketing journey platforms. Parse the raw webhook payload and extract user journey entry information regardless of format. Return JSON.",
        "Extract journey entry data from this webhook payload: {webhook_payload}. Find the user email, user name, journey name, entry segment, entry trigger type (event-based, segment-based, manual), user attributes (any custom fields), and entry timestamp regardless of how fields are named. Return user_email, user_name, journey_name, entry_segment, entry_trigger, user_attributes, entry_timestamp.",
        [
          { field: "user_email", as: "journey_email", type: "string" },
          { field: "user_name", as: "journey_user_name", type: "string" },
          { field: "journey_name", as: "journey_name", type: "string" },
          { field: "entry_segment", as: "entry_segment", type: "string" },
          { field: "entry_trigger", as: "entry_trigger", type: "string" },
          { field: "user_attributes", as: "user_attributes", type: "string" },
          { field: "entry_timestamp", as: "entry_timestamp", type: "string" },
        ],
      ),

      // 3. User attribute enricher (HubSpot)
      hubspotPostNode(
        "3",
        "Enrich user attributes from HubSpot",
        520,
        "crm/v3/objects/contacts/search",
        [
          {
            key: "filterGroups",
            value:
              '[{"filters":[{"propertyName":"email","operator":"EQ","value":"{journey_email}"}]}]',
          },
          {
            key: "properties",
            value:
              '["email","firstname","lastname","company","lifecyclestage","hs_analytics_revenue","hs_email_last_open_date","hs_analytics_num_page_views"]',
          },
        ],
        [
          {
            name: "results[0].properties.firstname",
            customName: "enriched_first_name",
            type: "string",
          },
          {
            name: "results[0].properties.company",
            customName: "enriched_company",
            type: "string",
          },
          {
            name: "results[0].properties.lifecyclestage",
            customName: "enriched_stage",
            type: "string",
          },
          {
            name: "results[0].properties.hs_analytics_revenue",
            customName: "enriched_revenue",
            type: "string",
          },
          {
            name: "results[0].properties.hs_email_last_open_date",
            customName: "enriched_last_open",
            type: "string",
          },
        ],
      ),

      // 4. Decision branch (condition)
      n("4", "condition", "Route by lifecycle: customer vs prospect", 760, {
        conditionDetails: {
          conditions: [
            {
              variable: "{enriched_stage}",
              operator: "equals",
              value: "customer",
            },
          ],
        },
      }),

      // 5. Wait/engage delay
      n("5", "delayAction", "Wait 2 hours for optimal send time", 1000, {
        delayDetails: { duration: 2, unit: "hours" },
      }),

      // 6. Multi-channel message (email)
      emailNode(
        "6",
        "Send journey stage-appropriate email",
        1240,
        "{journey_email}",
        "Your personalized journey with {company_name}, {enriched_first_name}",
        "Hi {enriched_first_name},\\n\\nWelcome to your {journey_name} journey! You have been selected based on your {entry_segment} profile.\\n\\nAs a {enriched_stage} at {enriched_company}, we have tailored this experience specifically for you.\\n\\nHere is what is coming next in your journey:\\n1. Personalized content recommendations\\n2. Exclusive insights based on your engagement\\n3. Direct access to our team\\n\\nStay tuned for more!\\n\\n{company_name}",
      ),

      // 7. Engagement check (condition)
      n("7", "condition", "Check engagement: has recent email opens", 1480, {
        conditionDetails: {
          conditions: [
            {
              variable: "{enriched_last_open}",
              operator: "not_equals",
              value: "",
            },
          ],
        },
      }),

      // 8. A/B split test (runCode)
      n("8", "runCode", "Calculate A/B split assignment", 1720, {
        runCodeDetails: {
          language: "javascript",
          code: `// A/B split test assignment for journey optimization
const email = vars.journey_email || "";
const hash = email.split("").reduce((a, c) => ((a << 5) - a + c.charCodeAt(0)) | 0, 0);
const bucket = Math.abs(hash) % 100;
const variant = bucket < 50 ? "A" : "B";
const stage = vars.enriched_stage || "subscriber";
const revenue = parseFloat(vars.enriched_revenue) || 0;
return {
  ab_variant: variant,
  ab_bucket: bucket,
  user_value_tier: revenue > 1000 ? "high" : revenue > 100 ? "medium" : "low",
  journey_step: "email_sent",
  engagement_tracked: vars.enriched_last_open ? "yes" : "no",
  split_timestamp: new Date().toISOString()
};`,
          outputVariables: [
            { name: "ab_variant", type: "string" },
            { name: "ab_bucket", type: "number" },
            { name: "user_value_tier", type: "string" },
            { name: "journey_step", type: "string" },
            { name: "engagement_tracked", type: "string" },
            { name: "split_timestamp", type: "string" },
          ],
        },
      }),

      // 9. Journey performance (Sheets)
      sheetsNode(
        "9",
        "Log journey performance metrics",
        1960,
        "JourneyAnalytics",
        '[["{entry_timestamp}","{journey_email}","{journey_name}","{entry_segment}","{entry_trigger}","{enriched_stage}","{enriched_company}","{ab_variant}","{user_value_tier}","{journey_step}","{engagement_tracked}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1240. WEBENGAGE PERSONALIZED PUSH CAMPAIGN (8 nodes)
  // ===================================================================

  "WebEngage Personalized Push Campaign": {
    summary:
      "Listens for behavioral event webhooks, extracts user context with AI, fetches user profile from HubSpot, matches behavioral segments with runCode, generates push content with AI, optimizes location-aware timing with runCode, dispatches push notification via Firebase API, and logs campaign analytics to Sheets.",
    nodes: [
      trigger("Webhook: Behavioral event captured", "webhook_app", {
        source: "webengage",
        event: "user.behavior_tracked",
        howItStarts:
          "Fires when WebEngage captures a significant behavioral event such as repeated product views, feature exploration, pricing page visits, or engagement pattern changes that warrant a personalized push notification",
        setup:
          "Configure WebEngage webhook for behavioral events. Set OpenAI API key for content generation. Provide HubSpot API key for user profile lookup. Configure Firebase Cloud Messaging key for push delivery. Set Google Sheets spreadsheet ID for analytics.",
        inputFields: [],
      }),

      // 2. AI extract user context
      aiNode(
        "2",
        "AI: Extract behavioral event context",
        280,
        "You are a data extraction assistant for behavioral analytics. Parse raw webhook payloads and extract user behavior signals for push notification personalization regardless of field naming. Return JSON.",
        "Extract behavioral event data from this webhook payload: {webhook_payload}. Find the user email, user device token (for push), behavior type (product_view, feature_explore, pricing_visit, cart_abandon, etc.), behavior count (times repeated), product or page context, user timezone, device platform (ios/android), and last active timestamp. Return user_email, device_token, behavior_type, behavior_count, behavior_context, user_timezone, device_platform, last_active.",
        [
          { field: "user_email", as: "push_user_email", type: "string" },
          { field: "device_token", as: "device_token", type: "string" },
          { field: "behavior_type", as: "behavior_type", type: "string" },
          { field: "behavior_count", as: "behavior_count", type: "string" },
          { field: "behavior_context", as: "behavior_context", type: "string" },
          { field: "user_timezone", as: "user_timezone", type: "string" },
          { field: "device_platform", as: "device_platform", type: "string" },
          { field: "last_active", as: "last_active", type: "string" },
        ],
      ),

      // 3. User profile fetcher (HubSpot)
      hubspotPostNode(
        "3",
        "Fetch user profile for push targeting",
        520,
        "crm/v3/objects/contacts/search",
        [
          {
            key: "filterGroups",
            value:
              '[{"filters":[{"propertyName":"email","operator":"EQ","value":"{push_user_email}"}]}]',
          },
          {
            key: "properties",
            value:
              '["email","firstname","lifecyclestage","hs_analytics_revenue","city","country"]',
          },
        ],
        [
          {
            name: "results[0].properties.firstname",
            customName: "push_first_name",
            type: "string",
          },
          {
            name: "results[0].properties.lifecyclestage",
            customName: "push_stage",
            type: "string",
          },
          {
            name: "results[0].properties.city",
            customName: "user_city",
            type: "string",
          },
          {
            name: "results[0].properties.country",
            customName: "user_country",
            type: "string",
          },
        ],
      ),

      // 4. Behavioral segment matcher (runCode)
      n("4", "runCode", "Match behavioral segment and urgency", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Match user to behavioral segment and determine push urgency
const behavior = (vars.behavior_type || "").toLowerCase();
const count = parseInt(vars.behavior_count) || 1;
const stage = (vars.push_stage || "subscriber").toLowerCase();
let segment = "general_interest";
let urgency = "normal";
if (behavior.includes("cart") || behavior.includes("abandon")) { segment = "cart_abandoner"; urgency = "high"; }
else if (behavior.includes("pricing")) { segment = "pricing_explorer"; urgency = "high"; }
else if (behavior.includes("product") && count >= 3) { segment = "product_enthusiast"; urgency = "medium"; }
else if (behavior.includes("feature")) { segment = "feature_explorer"; urgency = "medium"; }
const pushPriority = urgency === "high" ? "immediate" : "optimized_time";
return {
  behavioral_segment: segment,
  push_urgency: urgency,
  push_priority: pushPriority,
  repeat_behavior: count > 1 ? "yes" : "no",
  behavior_intensity: count >= 5 ? "high" : count >= 3 ? "medium" : "low",
  campaign_id: "push_" + Date.now().toString(36)
};`,
          outputVariables: [
            { name: "behavioral_segment", type: "string" },
            { name: "push_urgency", type: "string" },
            { name: "push_priority", type: "string" },
            { name: "repeat_behavior", type: "string" },
            { name: "behavior_intensity", type: "string" },
            { name: "campaign_id", type: "string" },
          ],
        },
      }),

      // 5. AI push content generator
      aiNode(
        "5",
        "AI: Generate personalized push content",
        1000,
        "You are a push notification copywriter. Write concise, action-driving push notifications that feel personal and timely. Adapt tone to the behavioral segment. Return JSON.",
        "Write a push notification for {push_first_name}. Segment: {behavioral_segment}. Behavior: {behavior_type} (x{behavior_count}). Context: {behavior_context}. Urgency: {push_urgency}. Stage: {push_stage}. City: {user_city}. Return push_title (max 50 chars, attention-grabbing), push_body (max 150 chars, action-driving), push_deep_link (relevant app deep link path), push_image_suggestion (image description for rich push).",
        [
          { field: "push_title", as: "push_title", type: "string" },
          { field: "push_body", as: "push_body", type: "string" },
          { field: "push_deep_link", as: "push_deep_link", type: "string" },
          {
            field: "push_image_suggestion",
            as: "push_image_desc",
            type: "string",
          },
        ],
      ),

      // 6. Location-aware timing optimizer (runCode)
      n("6", "runCode", "Optimize send time for user timezone", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate optimal push send time based on timezone and urgency
const tz = vars.user_timezone || "America/New_York";
const priority = vars.push_priority || "optimized_time";
const now = new Date();
let sendImmediately = priority === "immediate";
let optimalHour = 10;
if (priority === "optimized_time") {
  const currentHour = now.getUTCHours();
  sendImmediately = currentHour >= 13 && currentHour <= 23;
  optimalHour = sendImmediately ? currentHour : 14;
}
return {
  send_immediately: sendImmediately ? "yes" : "no",
  optimal_send_hour: optimalHour,
  send_timezone: tz,
  timing_strategy: sendImmediately ? "real_time" : "scheduled_optimal",
  push_timestamp: now.toISOString()
};`,
          outputVariables: [
            { name: "send_immediately", type: "string" },
            { name: "optimal_send_hour", type: "number" },
            { name: "send_timezone", type: "string" },
            { name: "timing_strategy", type: "string" },
            { name: "push_timestamp", type: "string" },
          ],
        },
      }),

      // 7. Push notification dispatcher (FCM API)
      n("7", "hitApi", "Send push notification via Firebase", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://fcm.googleapis.com/v1/projects/{firebase_project_id}/messages:send",
          headers: [
            { key: "Authorization", value: "Bearer {firebase_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "message",
              value:
                '{"token":"{device_token}","notification":{"title":"{push_title}","body":"{push_body}"},"data":{"deep_link":"{push_deep_link}","campaign_id":"{campaign_id}","segment":"{behavioral_segment}"},"android":{"priority":"high"},"apns":{"headers":{"apns-priority":"10"}}}',
            },
          ],
          variables: [
            { name: "name", customName: "fcm_message_id", type: "string" },
          ],
        },
      }),

      // 8. Campaign analytics (Sheets)
      sheetsNode(
        "8",
        "Log push campaign analytics",
        1720,
        "PushCampaigns",
        '[["{push_timestamp}","{campaign_id}","{push_user_email}","{behavioral_segment}","{behavior_type}","{behavior_count}","{push_urgency}","{timing_strategy}","{push_title}","{device_platform}","{fcm_message_id}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1241. WEBENGAGE RELAYS CAMPAIGN FLOW (8 nodes)
  // ===================================================================

  "WebEngage Relays Campaign Flow": {
    summary:
      "Listens for real-time event webhooks, extracts event data with AI, enriches event context from HubSpot, evaluates relay conditions via condition, sends instant messages via email, waits for follow-up timing with delay, checks follow-up engagement via condition, and sends conditional follow-up SMS.",
    nodes: [
      trigger("Webhook: Real-time relay event received", "webhook_app", {
        source: "webengage",
        event: "relay.triggered",
        howItStarts:
          "Fires when a WebEngage Relay captures a real-time customer event requiring immediate response, such as a transaction, status change, or time-sensitive interaction",
        setup:
          "Configure WebEngage Relay webhook for real-time events. Set OpenAI API key for data extraction. Provide HubSpot API key for context enrichment. Set email and SMS service credentials for multi-step relay delivery.",
        inputFields: [],
      }),

      // 2. AI extract event data
      aiNode(
        "2",
        "AI: Extract real-time relay event data",
        280,
        "You are a data extraction assistant for real-time event processing. Parse the raw webhook payload and extract event data for immediate relay messaging regardless of field naming conventions. Return JSON.",
        "Extract real-time relay event from this webhook payload: {webhook_payload}. Find the user email, user name, event type (transaction, status_change, alert, reminder), event details (amount, status, description), urgency level (low, medium, high, critical), timestamp, and any reference ID. Return user_email, user_name, relay_event_type, event_details, urgency_level, event_time, reference_id.",
        [
          { field: "user_email", as: "relay_email", type: "string" },
          { field: "user_name", as: "relay_user_name", type: "string" },
          { field: "relay_event_type", as: "relay_event_type", type: "string" },
          { field: "event_details", as: "event_details", type: "string" },
          { field: "urgency_level", as: "urgency_level", type: "string" },
          { field: "event_time", as: "event_time", type: "string" },
          { field: "reference_id", as: "reference_id", type: "string" },
        ],
      ),

      // 3. Event data enricher (HubSpot)
      hubspotPostNode(
        "3",
        "Enrich relay event with user context",
        520,
        "crm/v3/objects/contacts/search",
        [
          {
            key: "filterGroups",
            value:
              '[{"filters":[{"propertyName":"email","operator":"EQ","value":"{relay_email}"}]}]',
          },
          {
            key: "properties",
            value:
              '["email","firstname","lastname","phone","lifecyclestage","hs_email_last_open_date"]',
          },
        ],
        [
          {
            name: "results[0].properties.firstname",
            customName: "relay_first_name",
            type: "string",
          },
          {
            name: "results[0].properties.phone",
            customName: "relay_phone",
            type: "string",
          },
          {
            name: "results[0].properties.lifecyclestage",
            customName: "relay_stage",
            type: "string",
          },
          {
            name: "results[0].properties.hs_email_last_open_date",
            customName: "relay_last_open",
            type: "string",
          },
        ],
      ),

      // 4. Relay condition evaluator (condition)
      n("4", "condition", "Check urgency: not low priority", 760, {
        conditionDetails: {
          conditions: [
            {
              variable: "{urgency_level}",
              operator: "not_equals",
              value: "low",
            },
          ],
        },
      }),

      // 5. Instant message sender (email)
      emailNode(
        "5",
        "Send instant relay notification email",
        1000,
        "{relay_email}",
        "Action required: {relay_event_type} update - Ref #{reference_id}",
        "Hi {relay_first_name},\\n\\nThis is an important notification about your recent {relay_event_type}.\\n\\nDetails: {event_details}\\nReference: #{reference_id}\\nTime: {event_time}\\nUrgency: {urgency_level}\\n\\nIf you need to take action, please log in to your account or reply to this email.\\n\\n{company_name}",
      ),

      // 6. Follow-up wait (delay)
      n("6", "delayAction", "Wait 6 hours for follow-up check", 1240, {
        delayDetails: { duration: 6, unit: "hours" },
      }),

      // 7. Follow-up engagement check (condition)
      n("7", "condition", "Check if user has recent email activity", 1480, {
        conditionDetails: {
          conditions: [
            {
              variable: "{relay_last_open}",
              operator: "not_equals",
              value: "",
            },
          ],
        },
      }),

      // 8. Conditional follow-up sender (SMS)
      smsNode(
        "8",
        "Send SMS follow-up for unengaged relay",
        1720,
        "{relay_phone}",
        "Hi {relay_first_name}, important update on your {relay_event_type} (Ref #{reference_id}). Check your email or log in for details. - {company_name}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_2"),
    ],
  },

  // ===================================================================
  // 1242. WEBENGAGE REVENUE ATTRIBUTION TRACKER (8 nodes)
  // ===================================================================

  "WebEngage Revenue Attribution Tracker": {
    summary:
      "Listens for conversion event webhooks, extracts conversion data with AI, fetches touchpoint history from HubSpot, calculates multi-touch attribution with runCode, maps journey-level attribution with runCode, generates revenue insights with AI, writes dashboard data to Sheets, and sends weekly attribution reports via email.",
    nodes: [
      trigger("Webhook: Conversion event captured", "webhook_app", {
        source: "webengage",
        event: "conversion.completed",
        howItStarts:
          "Fires when WebEngage captures a conversion event (purchase, subscription, upgrade) that needs revenue attribution across the customer journey touchpoints",
        setup:
          "Configure WebEngage webhook for conversion events. Set OpenAI API key for data extraction and insights. Provide HubSpot API key for touchpoint history. Set Google Sheets spreadsheet ID for attribution dashboard.",
        inputFields: [],
      }),

      // 2. AI extract conversion data
      aiNode(
        "2",
        "AI: Extract conversion event details",
        280,
        "You are a data extraction assistant for revenue analytics. Parse the raw webhook payload and extract conversion data for attribution analysis regardless of field naming. Return JSON.",
        "Extract conversion data from this webhook payload: {webhook_payload}. Find the customer email, conversion type (purchase, subscription, upgrade, renewal), conversion value (revenue amount), currency, product or plan name, conversion timestamp, attribution window in days, and any UTM or campaign source data. Return customer_email, conversion_type, conversion_value, currency, product_name, conversion_time, attribution_window_days, campaign_source.",
        [
          { field: "customer_email", as: "conv_email", type: "string" },
          { field: "conversion_type", as: "conversion_type", type: "string" },
          { field: "conversion_value", as: "conversion_value", type: "string" },
          { field: "currency", as: "currency", type: "string" },
          { field: "product_name", as: "product_name", type: "string" },
          { field: "conversion_time", as: "conversion_time", type: "string" },
          {
            field: "attribution_window_days",
            as: "attribution_window",
            type: "string",
          },
          { field: "campaign_source", as: "campaign_source", type: "string" },
        ],
      ),

      // 3. Touchpoint history fetcher (HubSpot)
      hubspotPostNode(
        "3",
        "Fetch customer touchpoint history",
        520,
        "crm/v3/objects/contacts/search",
        [
          {
            key: "filterGroups",
            value:
              '[{"filters":[{"propertyName":"email","operator":"EQ","value":"{conv_email}"}]}]',
          },
          {
            key: "properties",
            value:
              '["email","firstname","lastname","hs_analytics_first_url","hs_analytics_last_url","hs_analytics_source","hs_analytics_num_page_views","hs_analytics_num_visits","hs_analytics_revenue","num_associated_deals"]',
          },
        ],
        [
          {
            name: "results[0].properties.hs_analytics_first_url",
            customName: "first_touch_url",
            type: "string",
          },
          {
            name: "results[0].properties.hs_analytics_last_url",
            customName: "last_touch_url",
            type: "string",
          },
          {
            name: "results[0].properties.hs_analytics_source",
            customName: "original_source",
            type: "string",
          },
          {
            name: "results[0].properties.hs_analytics_num_visits",
            customName: "total_visits",
            type: "string",
          },
          {
            name: "results[0].properties.hs_analytics_num_page_views",
            customName: "total_page_views",
            type: "string",
          },
          {
            name: "results[0].properties.num_associated_deals",
            customName: "deal_count",
            type: "string",
          },
        ],
      ),

      // 4. Multi-touch attribution calculator (runCode)
      n("4", "runCode", "Calculate multi-touch attribution model", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Multi-touch attribution calculation
const revenue = parseFloat(vars.conversion_value) || 0;
const visits = parseInt(vars.total_visits) || 1;
const source = vars.original_source || "direct";
// Position-based attribution: 40% first, 40% last, 20% middle
const firstTouchCredit = (revenue * 0.4).toFixed(2);
const lastTouchCredit = (revenue * 0.4).toFixed(2);
const middleTouchCredit = (revenue * 0.2).toFixed(2);
// Linear attribution for comparison
const linearPerTouch = visits > 0 ? (revenue / visits).toFixed(2) : revenue.toFixed(2);
return {
  total_revenue: revenue.toFixed(2),
  linear_per_touch: linearPerTouch,
  first_touch_credit: firstTouchCredit,
  last_touch_credit: lastTouchCredit,
  middle_touch_credit: middleTouchCredit,
  attribution_model: "position_based_40_20_40",
  touchpoint_count: visits,
  primary_source: source
};`,
          outputVariables: [
            { name: "total_revenue", type: "string" },
            { name: "linear_per_touch", type: "string" },
            { name: "first_touch_credit", type: "string" },
            { name: "last_touch_credit", type: "string" },
            { name: "middle_touch_credit", type: "string" },
            { name: "attribution_model", type: "string" },
            { name: "touchpoint_count", type: "number" },
            { name: "primary_source", type: "string" },
          ],
        },
      }),

      // 5. Journey-level attribution mapper (runCode)
      n("5", "runCode", "Map journey-level attribution paths", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Map attribution to journey stages
const lastUrl = vars.last_touch_url || "";
const source = vars.primary_source || "direct";
const convType = vars.conversion_type || "purchase";
const touchpoints = parseInt(vars.touchpoint_count) || 1;
let journeyType = "short";
if (touchpoints > 10) journeyType = "complex";
else if (touchpoints > 3) journeyType = "standard";
const avgDaysToConvert = Math.max(1, Math.round(touchpoints * 2.5));
return {
  journey_type: journeyType,
  journey_length: touchpoints,
  avg_days_to_convert: avgDaysToConvert,
  first_channel: source,
  converting_channel: lastUrl.includes("utm") ? "paid" : "organic",
  conversion_path: source + " > " + journeyType + " journey > " + convType,
  attribution_id: "attr_" + Date.now().toString(36)
};`,
          outputVariables: [
            { name: "journey_type", type: "string" },
            { name: "journey_length", type: "number" },
            { name: "avg_days_to_convert", type: "number" },
            { name: "first_channel", type: "string" },
            { name: "converting_channel", type: "string" },
            { name: "conversion_path", type: "string" },
            { name: "attribution_id", type: "string" },
          ],
        },
      }),

      // 6. AI revenue insights generator
      aiNode(
        "6",
        "AI: Generate revenue attribution insights",
        1240,
        "You are a revenue analytics expert. Analyze attribution data and provide actionable insights about conversion paths, channel effectiveness, and optimization opportunities. Return JSON.",
        "Analyze this conversion attribution: Email: {conv_email}. Type: {conversion_type}. Revenue: {currency} {total_revenue}. Product: {product_name}. Journey: {journey_type} ({journey_length} touchpoints, ~{avg_days_to_convert} days). Path: {conversion_path}. First channel: {first_channel}. Converting channel: {converting_channel}. Model: {attribution_model}. First touch credit: {first_touch_credit}. Last touch credit: {last_touch_credit}. Return key_insight (main attribution finding), channel_recommendation (budget allocation suggestion), journey_optimization (how to improve the path), revenue_trend (what this tells us about revenue patterns).",
        [
          { field: "key_insight", as: "key_insight", type: "string" },
          {
            field: "channel_recommendation",
            as: "channel_recommendation",
            type: "string",
          },
          {
            field: "journey_optimization",
            as: "journey_optimization",
            type: "string",
          },
          { field: "revenue_trend", as: "revenue_trend", type: "string" },
        ],
      ),

      // 7. Dashboard data writer (Sheets)
      sheetsNode(
        "7",
        "Write attribution data to dashboard",
        1480,
        "RevenueAttribution",
        '[["{conversion_time}","{attribution_id}","{conv_email}","{conversion_type}","{product_name}","{currency} {total_revenue}","{attribution_model}","{first_touch_credit}","{last_touch_credit}","{journey_type}","{journey_length}","{first_channel}","{converting_channel}","{conversion_path}"]]',
      ),

      // 8. Weekly revenue attribution email
      emailNode(
        "8",
        "Send attribution insight report",
        1720,
        "{analytics_team_email}",
        "Revenue Attribution: {currency} {total_revenue} {conversion_type} via {converting_channel}",
        "Revenue Attribution Report\\n\\nConversion: {conversion_type} - {product_name}\\nRevenue: {currency} {total_revenue}\\nCustomer: {conv_email}\\n\\nAttribution ({attribution_model}):\\n- First touch credit: {currency} {first_touch_credit} ({first_channel})\\n- Last touch credit: {currency} {last_touch_credit} ({converting_channel})\\n- Middle touch credit: {currency} {middle_touch_credit}\\n\\nJourney: {journey_type} ({journey_length} touchpoints, ~{avg_days_to_convert} days)\\nPath: {conversion_path}\\n\\nKey Insight: {key_insight}\\n\\nChannel Recommendation: {channel_recommendation}\\n\\nJourney Optimization: {journey_optimization}\\n\\nRevenue Trend: {revenue_trend}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1243. WEBENGAGE USER RETENTION CAMPAIGN (9 nodes)
  // ===================================================================

  "WebEngage User Retention Campaign": {
    summary:
      "Runs on a weekly schedule to identify at-risk users from HubSpot, classifies risk levels with runCode, fetches user context from HubSpot, generates retention content with AI, sends multi-channel retention emails, waits 5 days for engagement, re-checks engagement via condition, and escalates to Slack for critical cases.",
    nodes: [
      trigger("Schedule: Weekly at-risk user scan", "schedule", {
        schedule: "0 9 * * 2 (Tuesday at 9 AM)",
        howItStarts:
          "A weekly schedule identifies users showing at-risk signals such as declining feature usage, reduced login frequency, increased support tickets, or approaching contract end dates",
        setup:
          "Configure HubSpot API key for at-risk user identification and context lookup. Set OpenAI API key for retention content generation. Provide Slack webhook for escalation alerts. Set Google Sheets spreadsheet ID for retention tracking.",
        inputFields: [],
      }),

      // 2. HubSpot at-risk user identifier
      hubspotPostNode(
        "2",
        "Identify at-risk users in HubSpot",
        280,
        "crm/v3/objects/contacts/search",
        [
          {
            key: "filterGroups",
            value:
              '[{"filters":[{"propertyName":"lifecyclestage","operator":"EQ","value":"customer"},{"propertyName":"notes_last_updated","operator":"LT","value":"{fourteen_days_ago}"}]}]',
          },
          {
            key: "properties",
            value:
              '["email","firstname","lastname","company","lifecyclestage","hs_email_last_open_date","hs_analytics_num_page_views","hs_analytics_revenue"]',
          },
          { key: "limit", value: "20" },
        ],
        [
          {
            name: "results[0].properties.email",
            customName: "atrisk_email",
            type: "string",
          },
          {
            name: "results[0].properties.firstname",
            customName: "atrisk_first_name",
            type: "string",
          },
          {
            name: "results[0].properties.lastname",
            customName: "atrisk_last_name",
            type: "string",
          },
          {
            name: "results[0].properties.company",
            customName: "atrisk_company",
            type: "string",
          },
          {
            name: "results[0].id",
            customName: "atrisk_contact_id",
            type: "string",
          },
          {
            name: "results[0].properties.hs_analytics_revenue",
            customName: "atrisk_revenue",
            type: "string",
          },
          { name: "total", customName: "total_at_risk", type: "number" },
        ],
      ),

      // 3. Risk level classifier (runCode)
      n(
        "3",
        "runCode",
        "Classify user risk level and retention strategy",
        520,
        {
          runCodeDetails: {
            language: "javascript",
            code: `// Classify at-risk users by severity and determine retention approach
const totalAtRisk = parseInt(vars.total_at_risk) || 0;
const revenue = parseFloat(vars.atrisk_revenue) || 0;
let riskCategory = "moderate";
let retentionStrategy = "re_engagement_email";
if (revenue > 5000) { riskCategory = "critical"; retentionStrategy = "concierge_intervention"; }
else if (revenue > 1000) { riskCategory = "high"; retentionStrategy = "personalized_offer"; }
const escalationNeeded = riskCategory === "critical" ? "yes" : "no";
return {
  risk_category: riskCategory,
  retention_strategy: retentionStrategy,
  escalation_needed: escalationNeeded,
  cohort_size: totalAtRisk,
  revenue_at_risk: revenue.toFixed(2),
  retention_id: "ret_" + Date.now().toString(36),
  scan_date: new Date().toISOString().split("T")[0]
};`,
            outputVariables: [
              { name: "risk_category", type: "string" },
              { name: "retention_strategy", type: "string" },
              { name: "escalation_needed", type: "string" },
              { name: "cohort_size", type: "number" },
              { name: "revenue_at_risk", type: "string" },
              { name: "retention_id", type: "string" },
              { name: "scan_date", type: "string" },
            ],
          },
        },
      ),

      // 4. User context fetcher (HubSpot)
      hubspotGetNode(
        "4",
        "Fetch detailed user engagement history",
        760,
        "crm/v3/objects/contacts/{atrisk_contact_id}?properties=email,firstname,lastname,company,jobtitle,hs_email_last_open_date,hs_analytics_num_page_views,hs_analytics_num_visits,num_associated_deals,hs_lifecyclestage_customer_date",
        [
          {
            name: "properties.jobtitle",
            customName: "atrisk_title",
            type: "string",
          },
          {
            name: "properties.hs_analytics_num_page_views",
            customName: "atrisk_page_views",
            type: "string",
          },
          {
            name: "properties.hs_analytics_num_visits",
            customName: "atrisk_visits",
            type: "string",
          },
          {
            name: "properties.hs_lifecyclestage_customer_date",
            customName: "customer_since",
            type: "string",
          },
        ],
      ),

      // 5. AI retention content generator
      aiNode(
        "5",
        "AI: Generate retention campaign content",
        1000,
        "You are a customer retention specialist. Create personalized, empathetic re-engagement content that acknowledges the customer relationship, addresses potential pain points, and offers clear value to return. Return JSON.",
        "Write retention content for at-risk customer. Name: {atrisk_first_name} {atrisk_last_name}. Company: {atrisk_company}. Title: {atrisk_title}. Customer since: {customer_since}. Risk: {risk_category}. Strategy: {retention_strategy}. Revenue at risk: ${revenue_at_risk}. Page views: {atrisk_page_views}. Visits: {atrisk_visits}. Return email_subject (warm re-engagement subject), email_body (personalized retention email with value proposition), offer_details (specific retention offer if applicable), check_in_message (brief SMS-friendly check-in max 160 chars).",
        [
          { field: "email_subject", as: "retention_subject", type: "string" },
          { field: "email_body", as: "retention_body", type: "string" },
          { field: "offer_details", as: "retention_offer", type: "string" },
          { field: "check_in_message", as: "check_in_sms", type: "string" },
        ],
      ),

      // 6. Multi-channel retention sender (email)
      emailNode(
        "6",
        "Send retention re-engagement email",
        1240,
        "{atrisk_email}",
        "{retention_subject}",
        "{retention_body}",
      ),

      // 7. Delay 5 days
      n("7", "delayAction", "Wait 5 days for re-engagement response", 1480, {
        delayDetails: { duration: 5, unit: "days" },
      }),

      // 8. Engagement re-check (condition)
      n(
        "8",
        "condition",
        "Check: escalation needed for critical accounts",
        1720,
        {
          conditionDetails: {
            conditions: [
              {
                variable: "{escalation_needed}",
                operator: "equals",
                value: "yes",
              },
            ],
          },
        },
      ),

      // 9. Escalation path (Slack)
      slackNode(
        "9",
        "Escalate critical at-risk account to CS team",
        1960,
        ":warning: *Critical At-Risk Account - Escalation Required*\\n\\n*Customer:* {atrisk_first_name} {atrisk_last_name} ({atrisk_company})\\n*Title:* {atrisk_title}\\n*Risk Category:* {risk_category}\\n*Revenue at Risk:* ${revenue_at_risk}\\n*Customer Since:* {customer_since}\\n*Retention Strategy:* {retention_strategy}\\n*Offer Sent:* {retention_offer}\\n*Cohort Size:* {cohort_size} at-risk users this week\\n\\nRetention email was sent 5 days ago. Please initiate personal outreach immediately.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9", "output_1"),
    ],
  },

  // ===================================================================
  // 1244. WEBINAR ATTENDEE RETARGETING (7 nodes)
  // ===================================================================

  "Webinar Attendee Retargeting": {
    summary:
      "Listens for webinar end webhooks, extracts attendance data with AI, segments audiences with runCode, hashes emails with runCode for privacy-safe matching, syncs custom audiences to Meta API, selects segment-specific ad creatives with runCode, and logs campaign performance to Sheets.",
    nodes: [
      trigger("Webhook: Webinar concluded event", "webhook_app", {
        source: "webinar_platform",
        event: "webinar.ended",
        howItStarts:
          "Fires when a webinar concludes, capturing the attendee list, engagement data, attendance duration, poll responses, and Q&A participation for retargeting segmentation",
        setup:
          "Configure webinar platform webhook (Zoom/GoToWebinar) for webinar end events. Set OpenAI API key for data extraction. Provide Meta Marketing API token for custom audience sync. Set Google Sheets spreadsheet ID for campaign tracking.",
        inputFields: [],
      }),

      // 2. AI extract attendance data
      aiNode(
        "2",
        "AI: Extract webinar attendance data",
        280,
        "You are a data extraction assistant for webinar analytics. Parse the raw webhook payload and extract attendee data for retargeting regardless of field naming conventions. Return JSON.",
        "Extract webinar attendance data from this webhook payload: {webhook_payload}. Find the webinar title, webinar date, total registrants, total attendees, average attendance duration in minutes, top attendee email, top attendee name, attendance rate percentage, and primary topic. Return webinar_title, webinar_date, total_registrants, total_attendees, avg_duration_min, top_attendee_email, top_attendee_name, attendance_rate, primary_topic.",
        [
          { field: "webinar_title", as: "webinar_title", type: "string" },
          { field: "webinar_date", as: "webinar_date", type: "string" },
          {
            field: "total_registrants",
            as: "total_registrants",
            type: "string",
          },
          { field: "total_attendees", as: "total_attendees", type: "string" },
          { field: "avg_duration_min", as: "avg_duration", type: "string" },
          { field: "attendance_rate", as: "attendance_rate", type: "string" },
          { field: "top_attendee_email", as: "sample_email", type: "string" },
          { field: "top_attendee_name", as: "sample_name", type: "string" },
          { field: "primary_topic", as: "webinar_topic", type: "string" },
        ],
      ),

      // 3. Audience segmenter (runCode)
      n("3", "runCode", "Segment webinar audience for retargeting", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Segment webinar audience into retargeting groups
const registrants = parseInt(vars.total_registrants) || 0;
const attendees = parseInt(vars.total_attendees) || 0;
const avgDuration = parseInt(vars.avg_duration) || 0;
const noShows = registrants - attendees;
const engagedAttendees = Math.round(attendees * 0.6);
const dropOffs = attendees - engagedAttendees;
const attendanceRate = registrants > 0 ? ((attendees / registrants) * 100).toFixed(1) : "0";
return {
  segment_engaged: engagedAttendees,
  segment_dropoff: dropOffs,
  segment_noshow: noShows,
  calculated_attendance_rate: attendanceRate,
  total_retarget_audience: registrants,
  primary_segment: engagedAttendees > noShows ? "engaged_attendees" : "no_shows",
  campaign_id: "wrt_" + Date.now().toString(36)
};`,
          outputVariables: [
            { name: "segment_engaged", type: "number" },
            { name: "segment_dropoff", type: "number" },
            { name: "segment_noshow", type: "number" },
            { name: "calculated_attendance_rate", type: "string" },
            { name: "total_retarget_audience", type: "number" },
            { name: "primary_segment", type: "string" },
            { name: "campaign_id", type: "string" },
          ],
        },
      }),

      // 4. Email hasher (runCode)
      n("4", "runCode", "Prepare hashed emails for audience matching", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Prepare hashed email for Meta Custom Audience upload
const email = (vars.sample_email || "").toLowerCase().trim();
const normalizedEmail = email.replace(/\\s/g, "").toLowerCase();
const hashReady = normalizedEmail.length > 0 ? "yes" : "no";
return {
  normalized_email: normalizedEmail,
  hash_ready: hashReady,
  audience_name: "Webinar_" + (vars.webinar_title || "Unknown").replace(/[^a-zA-Z0-9]/g, "_").substring(0, 30) + "_" + vars.campaign_id,
  audience_description: "Retargeting audience from " + vars.webinar_title + " on " + vars.webinar_date,
  upload_format: "email_sha256"
};`,
          outputVariables: [
            { name: "normalized_email", type: "string" },
            { name: "hash_ready", type: "string" },
            { name: "audience_name", type: "string" },
            { name: "audience_description", type: "string" },
            { name: "upload_format", type: "string" },
          ],
        },
      }),

      // 5. Meta custom audience sync API
      n("5", "hitApi", "Create Meta custom audience for retargeting", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://graph.facebook.com/v18.0/{ad_account_id}/customaudiences",
          headers: [
            { key: "Authorization", value: "Bearer {meta_access_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "name", value: "{audience_name}" },
            { key: "description", value: "{audience_description}" },
            { key: "subtype", value: "CUSTOM" },
            { key: "customer_file_source", value: "USER_PROVIDED_ONLY" },
          ],
          variables: [
            { name: "id", customName: "meta_audience_id", type: "string" },
          ],
        },
      }),

      // 6. Segment-specific ad creative selector (runCode)
      n("6", "runCode", "Select ad creative strategy per segment", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Determine ad creative strategy based on primary segment
const segment = vars.primary_segment || "engaged_attendees";
const topic = vars.webinar_topic || "webinar";
let adStrategy = "";
let ctaType = "";
let budgetAllocation = "";
if (segment === "engaged_attendees") {
  adStrategy = "Bottom-funnel conversion: demo request or free trial with webinar-specific messaging";
  ctaType = "Schedule Demo";
  budgetAllocation = "50% engaged, 30% dropoffs, 20% no-shows";
} else {
  adStrategy = "Top-funnel replay: offer webinar replay access with lead magnet to capture no-shows";
  ctaType = "Watch Replay";
  budgetAllocation = "40% no-shows, 35% dropoffs, 25% engaged";
}
return {
  ad_strategy: adStrategy,
  cta_type: ctaType,
  budget_allocation: budgetAllocation,
  creative_theme: topic + " retargeting",
  campaign_objective: segment === "engaged_attendees" ? "CONVERSIONS" : "LEAD_GENERATION"
};`,
          outputVariables: [
            { name: "ad_strategy", type: "string" },
            { name: "cta_type", type: "string" },
            { name: "budget_allocation", type: "string" },
            { name: "creative_theme", type: "string" },
            { name: "campaign_objective", type: "string" },
          ],
        },
      }),

      // 7. Campaign performance (Sheets)
      sheetsNode(
        "7",
        "Log retargeting campaign setup",
        1480,
        "WebinarRetargeting",
        '[["{webinar_date}","{campaign_id}","{webinar_title}","{total_retarget_audience}","{segment_engaged}","{segment_dropoff}","{segment_noshow}","{calculated_attendance_rate}","{meta_audience_id}","{ad_strategy}","{cta_type}","{campaign_objective}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1245. WEBINAR REGISTRANT FOLLOW-UP (9 nodes)
  // ===================================================================

  "Webinar Registrant Follow-up": {
    summary:
      "Listens for registration webhooks, extracts registrant data with AI, enriches leads via Clearbit API, sends confirmation emails, creates CRM records in HubSpot, waits 1 day, sends reminder emails, checks attendance source via condition, and sends post-webinar follow-up emails.",
    nodes: [
      trigger("Webhook: Webinar registration received", "webhook_app", {
        source: "webinar_platform",
        event: "registration.created",
        howItStarts:
          "Fires when a new registrant signs up for an upcoming webinar, capturing their registration details, contact information, and any pre-webinar survey responses",
        setup:
          "Configure webinar platform webhook for new registrations. Set OpenAI API key for data extraction. Provide Clearbit API key for lead enrichment. Set HubSpot API key for CRM record creation. Configure email service for confirmation and follow-up sequences.",
        inputFields: [],
      }),

      // 2. AI extract registrant data
      aiNode(
        "2",
        "AI: Extract webinar registrant details",
        280,
        "You are a data extraction assistant for webinar platforms. Parse the raw webhook payload and extract registrant information regardless of format or field naming. Return JSON.",
        "Extract webinar registration data from this webhook payload: {webhook_payload}. Find the registrant email, registrant name, company name, job title, webinar title, webinar date and time, registration source (organic, paid, referral), and any survey responses regardless of how fields are named. Return registrant_email, registrant_name, registrant_company, registrant_title, webinar_title, webinar_datetime, registration_source, survey_responses.",
        [
          { field: "registrant_email", as: "reg_email", type: "string" },
          { field: "registrant_name", as: "reg_name", type: "string" },
          { field: "registrant_company", as: "reg_company", type: "string" },
          { field: "registrant_title", as: "reg_title", type: "string" },
          { field: "webinar_title", as: "reg_webinar_title", type: "string" },
          {
            field: "webinar_datetime",
            as: "reg_webinar_datetime",
            type: "string",
          },
          { field: "registration_source", as: "reg_source", type: "string" },
          { field: "survey_responses", as: "reg_survey", type: "string" },
        ],
      ),

      // 3. Lead enrichment (Clearbit API)
      n("3", "hitApi", "Enrich registrant via Clearbit", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://person.clearbit.com/v2/people/find?email={reg_email}",
          headers: [
            { key: "Authorization", value: "Bearer {clearbit_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "company.name",
              customName: "clearbit_company",
              type: "string",
            },
            {
              name: "company.metrics.employees",
              customName: "company_size",
              type: "string",
            },
            {
              name: "company.category.industry",
              customName: "industry",
              type: "string",
            },
            { name: "title", customName: "clearbit_title", type: "string" },
            {
              name: "company.metrics.annualRevenue",
              customName: "company_revenue",
              type: "string",
            },
          ],
        },
      }),

      // 4. Confirmation email sender
      emailNode(
        "4",
        "Send webinar registration confirmation",
        760,
        "{reg_email}",
        "You are registered: {reg_webinar_title}",
        "Hi {reg_name},\\n\\nYou are all set for {reg_webinar_title}!\\n\\nDetails:\\n- Date and Time: {reg_webinar_datetime}\\n- Add to your calendar to make sure you do not miss it\\n\\nCompany: {clearbit_company} ({industry})\\n\\nWhat to expect:\\n- Expert insights and live Q&A\\n- Actionable takeaways you can implement right away\\n- Networking opportunities with peers\\n\\nWe will send you a reminder 24 hours before the event.\\n\\nSee you there!\\n{company_name}",
      ),

      // 5. CRM record creator (HubSpot)
      hubspotPostNode(
        "5",
        "Create registrant record in HubSpot",
        1000,
        "crm/v3/objects/contacts",
        [
          {
            key: "properties",
            value:
              '{"email":"{reg_email}","firstname":"{reg_name}","company":"{clearbit_company}","jobtitle":"{clearbit_title}","industry":"{industry}","lifecyclestage":"lead","hs_lead_status":"WEBINAR_REGISTERED","numberofemployees":"{company_size}"}',
          },
        ],
        [{ name: "id", customName: "hubspot_contact_id", type: "string" }],
      ),

      // 6. Delay 1 day
      n("6", "delayAction", "Wait 1 day before reminder", 1240, {
        delayDetails: { duration: 1, unit: "days" },
      }),

      // 7. Reminder sequence email
      emailNode(
        "7",
        "Send webinar reminder email",
        1480,
        "{reg_email}",
        "Tomorrow: {reg_webinar_title} - Do not miss it, {reg_name}!",
        "Hi {reg_name},\\n\\nFriendly reminder: {reg_webinar_title} is coming up tomorrow!\\n\\nDate and Time: {reg_webinar_datetime}\\n\\nTo get the most out of it:\\n1. Prepare any questions you want answered\\n2. Test your audio/video setup\\n3. Block the time on your calendar\\n\\nAs someone in the {industry} industry, you will find the insights especially relevant.\\n\\nWe look forward to seeing you there!\\n{company_name}",
      ),

      // 8. Attendance detector (condition)
      n("8", "condition", "Check: registration source is paid", 1720, {
        conditionDetails: {
          conditions: [
            { variable: "{reg_source}", operator: "equals", value: "paid" },
          ],
        },
      }),

      // 9. Post-webinar follow-up email
      emailNode(
        "9",
        "Send post-webinar follow-up email",
        1960,
        "{reg_email}",
        "Thank you for joining {reg_webinar_title}, {reg_name}",
        "Hi {reg_name},\\n\\nThank you for your interest in {reg_webinar_title}!\\n\\nHere is what is next:\\n\\n1. Replay: We will share the recording within 24 hours\\n2. Resources: Check out the slides and supplementary materials\\n3. Next steps: Book a personalized consultation based on what you learned\\n\\nYour profile:\\n- Company: {clearbit_company} ({company_size} employees)\\n- Industry: {industry}\\n- Survey feedback: {reg_survey}\\n\\nWant to discuss how these insights apply to your team? Reply to schedule a quick call.\\n\\nBest,\\n{company_name}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9", "output_1"),
    ],
  },

  // ===================================================================
  // 1246. WEEKLY COMMUNITY ROUNDUP POST (8 nodes)
  // ===================================================================

  "Weekly Community Roundup Post": {
    summary:
      "Runs on a weekly schedule, fetches community data from Discourse API, calculates engagement metrics with runCode, identifies top contributors with runCode, composes a community roundup with AI, routes through a content quality review approval gate, publishes via email newsletter, and logs community health to Sheets.",
    nodes: [
      trigger("Schedule: Weekly community roundup", "schedule", {
        schedule: "0 10 * * 5 (Friday at 10 AM)",
        howItStarts:
          "A weekly schedule aggregates community activity including top discussions, most helpful answers, new member highlights, and trending topics for a curated roundup post",
        setup:
          "Configure Discourse API key for community data access. Set OpenAI API key for roundup composition. Set Google Sheets spreadsheet ID for community health tracking. Configure email service for newsletter distribution.",
        inputFields: [],
      }),

      // 2. Community API data fetcher (Discourse)
      n("2", "hitApi", "Fetch weekly community activity from Discourse", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://{discourse_domain}/top.json?period=weekly",
          headers: [
            { key: "Api-Key", value: "{discourse_api_key}" },
            { key: "Api-Username", value: "system" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "topic_list.topics[0].title",
              customName: "top_topic_title",
              type: "string",
            },
            {
              name: "topic_list.topics[0].posts_count",
              customName: "top_topic_posts",
              type: "number",
            },
            {
              name: "topic_list.topics[0].like_count",
              customName: "top_topic_likes",
              type: "number",
            },
            {
              name: "topic_list.topics[0].views",
              customName: "top_topic_views",
              type: "number",
            },
            {
              name: "topic_list.topics[1].title",
              customName: "second_topic_title",
              type: "string",
            },
            {
              name: "topic_list.topics[1].posts_count",
              customName: "second_topic_posts",
              type: "number",
            },
            {
              name: "topic_list.topics.length",
              customName: "total_active_topics",
              type: "number",
            },
          ],
        },
      }),

      // 3. Engagement metrics calculator (runCode)
      n("3", "runCode", "Calculate community engagement metrics", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate weekly community health metrics
const topPosts = parseInt(vars.top_topic_posts) || 0;
const topLikes = parseInt(vars.top_topic_likes) || 0;
const topViews = parseInt(vars.top_topic_views) || 0;
const secondPosts = parseInt(vars.second_topic_posts) || 0;
const totalTopics = parseInt(vars.total_active_topics) || 0;
const totalPosts = topPosts + secondPosts;
const avgPostsPerTopic = totalTopics > 0 ? (totalPosts / Math.min(totalTopics, 2)).toFixed(1) : "0";
const engagementRate = topViews > 0 ? ((topLikes / topViews) * 100).toFixed(1) : "0";
const healthScore = Math.min(100, totalTopics * 5 + topLikes * 2 + topPosts * 3);
const weekLabel = new Date().toISOString().split("T")[0];
return {
  weekly_total_posts: totalPosts,
  avg_posts_per_topic: avgPostsPerTopic,
  top_engagement_rate: engagementRate,
  community_health_score: healthScore,
  active_topics_count: totalTopics,
  week_label: weekLabel,
  roundup_id: "roundup_" + Date.now().toString(36)
};`,
          outputVariables: [
            { name: "weekly_total_posts", type: "number" },
            { name: "avg_posts_per_topic", type: "string" },
            { name: "top_engagement_rate", type: "string" },
            { name: "community_health_score", type: "number" },
            { name: "active_topics_count", type: "number" },
            { name: "week_label", type: "string" },
            { name: "roundup_id", type: "string" },
          ],
        },
      }),

      // 4. Top contributor identifier (runCode)
      n("4", "runCode", "Identify top contributors and highlights", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Identify highlights for the community roundup
const topTitle = vars.top_topic_title || "No top topic";
const topPosts = vars.top_topic_posts || 0;
const topLikes = vars.top_topic_likes || 0;
const secondTitle = vars.second_topic_title || "No second topic";
const healthScore = parseInt(vars.community_health_score) || 0;
const trend = healthScore > 70 ? "thriving" : healthScore > 40 ? "steady" : "needs_attention";
return {
  highlight_topic_1: topTitle + " (" + topPosts + " posts, " + topLikes + " likes)",
  highlight_topic_2: secondTitle,
  community_trend: trend,
  spotlight_text: "This week's most engaging discussion: " + topTitle,
  contributor_shoutout: "Thank you to everyone who contributed to " + topTitle + " — the most active thread this week!",
  trend_label: trend === "thriving" ? "Community is thriving" : trend === "steady" ? "Community is steady" : "Community needs attention"
};`,
          outputVariables: [
            { name: "highlight_topic_1", type: "string" },
            { name: "highlight_topic_2", type: "string" },
            { name: "community_trend", type: "string" },
            { name: "spotlight_text", type: "string" },
            { name: "contributor_shoutout", type: "string" },
            { name: "trend_label", type: "string" },
          ],
        },
      }),

      // 5. AI roundup composer
      aiNode(
        "5",
        "AI: Compose weekly community roundup",
        1000,
        "You are a community manager writing engaging weekly roundup newsletters. Celebrate activity, highlight contributors, and encourage continued participation. Keep the tone warm and inclusive. Return JSON.",
        "Write a weekly community roundup for the week of {week_label}. Health score: {community_health_score}/100 ({community_trend}). Active topics: {active_topics_count}. Total posts: {weekly_total_posts}. Top engagement rate: {top_engagement_rate}%. Highlight 1: {highlight_topic_1}. Highlight 2: {highlight_topic_2}. Spotlight: {spotlight_text}. Shoutout: {contributor_shoutout}. Trend: {trend_label}. Return roundup_subject (engaging newsletter subject), roundup_body (full roundup email with sections for highlights, stats, shoutouts, and CTA to participate), social_summary (short social media post, 280 chars).",
        [
          { field: "roundup_subject", as: "roundup_subject", type: "string" },
          { field: "roundup_body", as: "roundup_body", type: "string" },
          { field: "social_summary", as: "social_summary", type: "string" },
        ],
      ),

      // 6. Content quality review (approval gate)
      n("6", "approvalGate", "Community Manager Approval", 1240, {
        approvalGateDetails: {
          approvers: ["community-manager@company.com"],
          message:
            "Weekly community roundup ready for review:\\n\\nSubject: {roundup_subject}\\nHealth Score: {community_health_score}/100\\nTrend: {community_trend}\\nActive Topics: {active_topics_count}\\n\\nPlease review the content and approve for distribution.",
          timeoutHours: 12,
        },
      }),

      // 7. Multi-channel publisher (email)
      emailNode(
        "7",
        "Send community roundup newsletter",
        1480,
        "{community_newsletter_list}",
        "{roundup_subject}",
        "{roundup_body}",
      ),

      // 8. Community health logger (Sheets)
      sheetsNode(
        "8",
        "Log community health metrics",
        1720,
        "CommunityHealth",
        '[["{week_label}","{roundup_id}","{community_health_score}","{community_trend}","{active_topics_count}","{weekly_total_posts}","{avg_posts_per_topic}","{top_engagement_rate}","{highlight_topic_1}","{highlight_topic_2}","published"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1247. WEEKLY GOOGLE ADS DIGEST (8 nodes)
  // ===================================================================

  "Weekly Google Ads Digest": {
    summary:
      "Runs every Monday on schedule, fetches Google Ads performance data via API, calculates week-over-week comparisons with runCode, generates narrative insights with AI, highlights top and bottom performers with runCode, builds visual report data in Sheets, publishes digest to Slack, and sends a full email report.",
    nodes: [
      trigger("Schedule: Monday Google Ads digest", "schedule", {
        schedule: "0 8 * * 1 (Monday at 8 AM)",
        howItStarts:
          "A weekly schedule pulls the previous week's Google Ads performance data across all campaigns, calculating spend, conversions, CPA, ROAS, and click metrics for a comprehensive digest",
        setup:
          "Configure Google Ads API developer token and OAuth credentials. Set OpenAI API key for narrative insights. Provide Slack webhook for team digest. Set Google Sheets spreadsheet ID for visual reports. Configure email service for detailed reports.",
        inputFields: [],
      }),

      // 2. Google Ads API data fetcher
      n("2", "hitApi", "Fetch weekly Google Ads performance", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://googleads.googleapis.com/v15/customers/{google_ads_customer_id}/googleAds:searchStream",
          headers: [
            { key: "Authorization", value: "Bearer {google_ads_token}" },
            { key: "developer-token", value: "{google_ads_dev_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "query",
              value:
                "SELECT campaign.name, campaign.id, metrics.cost_micros, metrics.conversions, metrics.clicks, metrics.impressions, metrics.ctr, metrics.average_cpc, metrics.conversions_value FROM campaign WHERE segments.date DURING LAST_7_DAYS AND campaign.status = 'ENABLED' ORDER BY metrics.cost_micros DESC LIMIT 20",
            },
          ],
          variables: [
            {
              name: "results[0].campaign.name",
              customName: "top_campaign_name",
              type: "string",
            },
            {
              name: "results[0].metrics.costMicros",
              customName: "top_cost_micros",
              type: "string",
            },
            {
              name: "results[0].metrics.conversions",
              customName: "top_conversions",
              type: "string",
            },
            {
              name: "results[0].metrics.clicks",
              customName: "top_clicks",
              type: "string",
            },
            {
              name: "results[0].metrics.impressions",
              customName: "top_impressions",
              type: "string",
            },
            {
              name: "results[0].metrics.ctr",
              customName: "top_ctr",
              type: "string",
            },
            {
              name: "results[0].metrics.conversionsValue",
              customName: "top_conv_value",
              type: "string",
            },
            {
              name: "results.length",
              customName: "total_campaigns",
              type: "number",
            },
          ],
        },
      }),

      // 3. Week-over-week comparator (runCode)
      n("3", "runCode", "Calculate week-over-week performance changes", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate key metrics and format for reporting
const costMicros = parseInt(vars.top_cost_micros) || 0;
const spend = (costMicros / 1000000).toFixed(2);
const conversions = parseFloat(vars.top_conversions) || 0;
const clicks = parseInt(vars.top_clicks) || 0;
const impressions = parseInt(vars.top_impressions) || 0;
const ctr = parseFloat(vars.top_ctr) || 0;
const convValue = parseFloat(vars.top_conv_value) || 0;
const cpa = conversions > 0 ? (costMicros / 1000000 / conversions).toFixed(2) : "N/A";
const roas = costMicros > 0 ? (convValue / (costMicros / 1000000)).toFixed(2) : "N/A";
const totalCampaigns = parseInt(vars.total_campaigns) || 0;
return {
  formatted_spend: spend,
  formatted_cpa: cpa,
  formatted_roas: roas,
  conversion_count: conversions,
  click_count: clicks,
  impression_count: impressions,
  formatted_ctr: (ctr * 100).toFixed(2),
  conversion_value: convValue.toFixed(2),
  campaigns_active: totalCampaigns,
  report_week: new Date(Date.now() - 7*24*60*60*1000).toISOString().split("T")[0] + " to " + new Date().toISOString().split("T")[0],
  digest_id: "gads_" + Date.now().toString(36)
};`,
          outputVariables: [
            { name: "formatted_spend", type: "string" },
            { name: "formatted_cpa", type: "string" },
            { name: "formatted_roas", type: "string" },
            { name: "conversion_count", type: "number" },
            { name: "click_count", type: "number" },
            { name: "impression_count", type: "number" },
            { name: "formatted_ctr", type: "string" },
            { name: "conversion_value", type: "string" },
            { name: "campaigns_active", type: "number" },
            { name: "report_week", type: "string" },
            { name: "digest_id", type: "string" },
          ],
        },
      }),

      // 4. AI performance narrative generator
      aiNode(
        "4",
        "AI: Generate performance narrative insights",
        760,
        "You are a Google Ads performance analyst. Write clear, actionable narrative insights about weekly campaign performance. Focus on what changed, why it matters, and what to do next. Return JSON.",
        "Analyze this week's Google Ads performance. Top campaign: {top_campaign_name}. Spend: ${formatted_spend}. Conversions: {conversion_count}. CPA: ${formatted_cpa}. ROAS: {formatted_roas}x. Clicks: {click_count}. Impressions: {impression_count}. CTR: {formatted_ctr}%. Conv value: ${conversion_value}. Active campaigns: {campaigns_active}. Week: {report_week}. Return executive_summary (2-3 sentence overview), key_wins (top positive findings), concerns (areas needing attention), recommendations (3 specific action items), budget_advice (should spend increase, decrease, or shift).",
        [
          {
            field: "executive_summary",
            as: "executive_summary",
            type: "string",
          },
          { field: "key_wins", as: "key_wins", type: "string" },
          { field: "concerns", as: "concerns", type: "string" },
          { field: "recommendations", as: "recommendations", type: "string" },
          { field: "budget_advice", as: "budget_advice", type: "string" },
        ],
      ),

      // 5. Top/bottom performer highlighter (runCode)
      n("5", "runCode", "Highlight top and bottom performers", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Format top/bottom performer highlights
const topCampaign = vars.top_campaign_name || "Unknown";
const spend = vars.formatted_spend || "0";
const conversions = vars.conversion_count || 0;
const cpa = vars.formatted_cpa || "N/A";
const roas = vars.formatted_roas || "N/A";
const topPerformer = topCampaign + ": $" + spend + " spend, " + conversions + " conversions, $" + cpa + " CPA, " + roas + "x ROAS";
const efficiency = parseFloat(roas) > 3 ? "excellent" : parseFloat(roas) > 1.5 ? "good" : "needs_improvement";
return {
  top_performer_summary: topPerformer,
  efficiency_rating: efficiency,
  spend_efficiency: parseFloat(cpa) < 50 ? "efficient" : parseFloat(cpa) < 100 ? "moderate" : "high_cost",
  report_timestamp: new Date().toISOString(),
  week_number: "W" + Math.ceil((new Date().getDate()) / 7)
};`,
          outputVariables: [
            { name: "top_performer_summary", type: "string" },
            { name: "efficiency_rating", type: "string" },
            { name: "spend_efficiency", type: "string" },
            { name: "report_timestamp", type: "string" },
            { name: "week_number", type: "string" },
          ],
        },
      }),

      // 6. Visual report builder (Sheets)
      sheetsNode(
        "6",
        "Write weekly report data to Sheets",
        1240,
        "GoogleAdsWeekly",
        '[["{report_week}","{digest_id}","{week_number}","{campaigns_active}","{formatted_spend}","{conversion_count}","{formatted_cpa}","{formatted_roas}","{click_count}","{impression_count}","{formatted_ctr}","{conversion_value}","{efficiency_rating}","{top_campaign_name}"]]',
      ),

      // 7. Slack digest publisher
      slackNode(
        "7",
        "Publish Google Ads digest to Slack",
        1480,
        ":bar_chart: *Weekly Google Ads Digest - {report_week}*\\n\\n*Summary:* {executive_summary}\\n\\n*Key Metrics:*\\n- Spend: ${formatted_spend} | Conversions: {conversion_count}\\n- CPA: ${formatted_cpa} | ROAS: {formatted_roas}x\\n- Clicks: {click_count} | CTR: {formatted_ctr}%\\n- Conv Value: ${conversion_value}\\n\\n*Top Performer:* {top_performer_summary}\\n*Efficiency:* {efficiency_rating} ({spend_efficiency})\\n\\n*Wins:* {key_wins}\\n*Concerns:* {concerns}\\n*Budget:* {budget_advice}",
      ),

      // 8. Email report sender
      emailNode(
        "8",
        "Send weekly Google Ads report email",
        1720,
        "{marketing_team_email}",
        "Google Ads Weekly Digest - {report_week} ({week_number})",
        "Google Ads Weekly Performance Digest\\nPeriod: {report_week}\\n\\nExecutive Summary:\\n{executive_summary}\\n\\nKey Metrics:\\n- Total Spend: ${formatted_spend}\\n- Conversions: {conversion_count} (Value: ${conversion_value})\\n- CPA: ${formatted_cpa}\\n- ROAS: {formatted_roas}x\\n- Clicks: {click_count}\\n- Impressions: {impression_count}\\n- CTR: {formatted_ctr}%\\n- Active Campaigns: {campaigns_active}\\n\\nTop Performer:\\n{top_performer_summary}\\nEfficiency Rating: {efficiency_rating}\\n\\nKey Wins:\\n{key_wins}\\n\\nConcerns:\\n{concerns}\\n\\nRecommendations:\\n{recommendations}\\n\\nBudget Advice:\\n{budget_advice}\\n\\nFull data available in the Google Ads Weekly sheet.\\n\\n{company_name} Marketing Analytics",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },
};
